import React, { useEffect } from 'react';
import { getOnlineIconUrl } from '../utils/onlineIconsMapping';

const SignatureDisplay = ({ formData }) => {
  
  // Valeurs par défaut
  const defaults = {
    nom: 'Jean Dupont',
    poste: 'Directeur Marketing',
    entreprise: 'Mon Entreprise',
    telephone: '06 40 25 23 12',
    email: 'jean.dupont@entreprise.com',
    siteWeb: 'www.entreprise.com',
    adresse: '123 Rue de la Paix, 75001 Paris',
    photoUrl: 'https://images.pexels.com/photos/27897903/pexels-photo-27897903.jpeg'
  };

  // Charger le template HTML pour le mode centré si nécessaire
  useEffect(() => {
    // Template loading logic removed as it's no longer needed
  }, [formData?.template, formData?.barrePosition]);

  // Fonction pour générer les boutons CTA
  const renderCTAButtons = () => {
    if (!formData.ctaButtons || formData.ctaButtons.length === 0) return null;

    const getPositionStyle = (position) => {
      switch (position) {
        case 'gauche':
        case 'left':
          return 'flex-start';
        case 'droite':
        case 'right':
          return 'flex-end';
        case 'milieu':
        case 'center':
        default:
          return 'center';
      }
    };

    return (
      <div style={{ 
        marginTop: '15px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        justifyContent: getPositionStyle(formData.ctaButtons[0]?.position || 'milieu')
      }}>
        {formData.ctaButtons.map((button, index) => {
          const paddingVal = button.padding ? `${button.padding}px ${parseInt(button.padding, 10) * 1.5}px` : '10px 15px';
          const borderRadiusVal = button.borderRadius ? `${button.borderRadius}px` : '8px';
          const fontSizeVal = button.fontSize ? `${button.fontSize}px` : '14px';
          return (
            <a
              key={index}
              href={button.link || '#'}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: paddingVal,
                backgroundColor: button.backgroundColor || '#007bff',
                color: button.textColor || '#ffffff',
                textDecoration: 'none',
                borderRadius: borderRadiusVal,
                fontSize: fontSizeVal,
                fontFamily: button.fontFamily || 'Arial, sans-serif',
                fontWeight: button.fontWeight || 'normal',
                fontStyle: button.fontStyle || 'normal',
                border: 'none',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => { e.target.style.opacity = '0.8'; }}
              onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
            >
              {button.text || 'Bouton'}
            </a>
          );
        })}
      </div>
    );
  };

  // Fonction pour générer les icônes de réseaux sociaux
  const renderSocialIcons = () => {
    const activeSocials = [];

    // Utiliser l'ordre défini dans socialOrder si disponible
    if (formData.socialOrder && formData.socialOrder.length > 0) {
      formData.socialOrder.forEach(orderItem => {
        if (orderItem.type === 'predefined') {
          const url = formData[`social_${orderItem.key}`];
          // Afficher l'icône si l'URL existe (même si c'est une URL par défaut)
          if (url) {
            const style = formData[`social_${orderItem.key}_style`] || 'default';
            activeSocials.push({
              key: orderItem.key,
              url: url,
              networkUrl: orderItem.data.url,
              style: style
            });
          }
        } else if (orderItem.type === 'other') {
          const network = formData.otherNetworks?.find(n => n.id === orderItem.key);
          if (network && network.url && network.url.trim()) {
            const style = formData[`social_other_${network.id}_style`] || 'default';
            activeSocials.push({
              key: `other_${network.id}`,
              url: network.url,
              networkUrl: network.networkUrl,
              name: network.name,
              style: style
            });
          }
        }
      });
    } else {
      // Fallback vers l'ancien comportement si socialOrder n'est pas disponible
      const socialNetworks = [
        { key: 'linkedin', networkUrl: 'https://linkedin.com' },
        { key: 'facebook', networkUrl: 'https://facebook.com' },
        { key: 'twitter', networkUrl: 'https://x.com' },
        { key: 'instagram', networkUrl: 'https://instagram.com' },
        { key: 'youtube', networkUrl: 'https://youtube.com' },
        { key: 'tiktok', networkUrl: 'https://tiktok.com' }
      ];

      // Ajouter les réseaux prédéfinis qui ont une URL
      socialNetworks.forEach(network => {
        const url = formData[`social_${network.key}`];
        // Afficher l'icône si l'URL existe (même si c'est une URL par défaut)
        if (url) {
          const style = formData[`social_${network.key}_style`] || 'default';
          activeSocials.push({
            key: network.key,
            url: url,
            networkUrl: network.networkUrl,
            style: style
          });
        }
      });

      // Ajouter les autres réseaux dynamiques qui ont une URL
      if (formData.otherNetworks) {
        formData.otherNetworks.forEach(network => {
          if (network.url && network.url.trim() && network.name) {
            const style = formData[`social_other_${network.id}_style`] || 'default';
            activeSocials.push({
              key: `other_${network.id}`,
              url: network.url,
              networkUrl: network.networkUrl,
              name: network.name,
              style: style
            });
          }
        });
      }
    }

    if (activeSocials.length === 0) return null;

    return (
      <div style={{ marginTop: '8px' }}>
        {activeSocials.map((social, index) => {
          // Extraire le nom du réseau pour les clés 'other_xxx'
          const getNetworkName = (key) => {
            if (key.startsWith('other_')) {
              // Pour les réseaux 'other', utiliser le nom du réseau
              const network = formData.otherNetworks?.find(n => `other_${n.id}` === key);
              return network ? network.name.toLowerCase() : key.replace('other_', '');
            }
            return key;
          };

          const networkName = getNetworkName(social.key);

          const iconSize = formData.iconSize || 32;
          const iconShape = formData.iconShape || 'rond';
          const iconColor = formData.iconColor || '#FFFFFF';
          const iconBackgroundColor = formData.iconBackgroundColor || '#0077B5';
          
          const iconUrl = getOnlineIconUrl(networkName, {
            color: iconColor,
            size: iconSize
          });
          
          if (!iconUrl) return null;
          
          const borderRadius = iconShape === 'rond' ? '50%' : iconShape === 'arrondi' ? '20%' : '0%';
          
          const isLastIcon = index === activeSocials.length - 1;
          
          return (
            <a 
              key={social.key || index}
              href={social.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                textDecoration: 'none',
                display: 'inline-block',
                marginRight: isLastIcon ? '0' : '8px'
              }}
            >
              <img 
                src={iconUrl}
                alt={networkName}
                title={networkName}
                style={{
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  backgroundColor: iconBackgroundColor,
                  borderRadius: borderRadius,
                  padding: '4px',
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }}
              />
            </a>
          );
        })}
      </div>
    );
  };

  const {
    nom,
    poste,
    departement,
    entreprise,
    telephone,
    email,
    siteWeb,
    adresse,
    photoUrl,
    banniereUrl,
    lienBanniere,
    photoSize = 100,
    bannerSize = 200,
    police = 'Figtree',
    taillePolice = 12,
    couleurNom = '#2c3e50',
    couleurAccent = '#3498db',
    template,
    contactOrder,
    customFields = []
  } = formData || {};

  // Utiliser les valeurs par défaut si les champs sont vides
  const displayData = {
    nom: nom || defaults.nom,
    poste: poste || defaults.poste,
    departement: departement || '',
    entreprise: entreprise || defaults.entreprise,
    telephone: telephone || defaults.telephone,
    email: email || defaults.email,
    siteWeb: siteWeb || defaults.siteWeb,
    adresse: adresse || defaults.adresse,
    photoUrl: photoUrl || defaults.photoUrl
  };

  // Styles de base pour tous les templates
  const baseStyles = {
    fontFamily: `'${police}', Arial, sans-serif`,
    fontSize: `${taillePolice}px`,
    lineHeight: '1.4',
    color: '#333333'
  };

  // Fonction utilitaire pour rendre les champs de contact selon l'ordre défini
  const renderContactFields = (style = 'default') => {
    // Récupérer les options créatives pour les emojis
    const creatifOptions = formData.creatif || {};
    const emojisActives = creatifOptions.emojisEnabled !== false;
    
    // Récupérer les options corporate pour les labels
    const corporateOptions = formData.corporate || {};
    const labelsDisplay = corporateOptions.labelsDisplay || 'labels';
    
    const contactFields = {
      telephone: {
         icon: style === 'creatif' && emojisActives ? '📱' : '📞',
         value: displayData.telephone,
         render: (iconStyle, textStyle) => {
         const marginBottom = style === 'clean' ? '2px' : '3px';
         const icon = style === 'creatif' && emojisActives ? '📱 ' : (iconStyle ? '📞 ' : '');
         
         // Gestion des labels pour le style corporate
         let label = '';
         let showIcon = iconStyle;
         if (style === 'corporate') {
           switch (labelsDisplay) {
             case 'labels':
               label = 'Tél: ';
               showIcon = false;
               break;
             case 'pictogrammes':
               label = '';
               showIcon = true;
               break;
             case 'non-visibles':
               label = '';
               showIcon = false;
               break;
             default:
               break;
           }
         }
         
         return displayData.telephone ? (
           <div style={{ marginBottom }}>
             <span style={textStyle}>{label}{showIcon ? icon : ''}{displayData.telephone}</span>
           </div>
         ) : null;
       }
       },
       email: {
         icon: style === 'creatif' && emojisActives ? '📧' : '📧',
         value: displayData.email,
         render: (iconStyle, textStyle) => {
           const marginBottom = style === 'clean' ? '2px' : '3px';
           const icon = style === 'creatif' && emojisActives ? '📧 ' : (iconStyle ? '📧 ' : '');
           
           // Gestion des labels pour le style corporate
           let label = '';
           let showIcon = iconStyle;
           if (style === 'corporate') {
             switch (labelsDisplay) {
               case 'labels':
                 label = 'Email: ';
                 showIcon = false;
                 break;
               case 'pictogrammes':
                 label = '';
                 showIcon = true;
                 break;
               case 'non-visibles':
                 label = '';
                 showIcon = false;
                 break;
               default:
                 break;
             }
           }
           
           return displayData.email ? (
             <div style={{ marginBottom }}>
               <a href={`mailto:${displayData.email}`} style={{ ...textStyle, textDecoration: 'none' }}>
                 {label}{showIcon ? icon : ''}{displayData.email}
               </a>
             </div>
           ) : null;
         }
       },
       siteWeb: {
         icon: style === 'creatif' && emojisActives ? '🌍' : '🌐',
         value: displayData.siteWeb,
         render: (iconStyle, textStyle) => {
           const marginBottom = style === 'clean' ? '2px' : '3px';
           const icon = style === 'creatif' && emojisActives ? '🌍 ' : (iconStyle ? '🌐 ' : '');
           
           // Gestion des labels pour le style corporate
           let label = '';
           let showIcon = iconStyle;
           if (style === 'corporate') {
             switch (labelsDisplay) {
               case 'labels':
                 label = 'Web: ';
                 showIcon = false;
                 break;
               case 'pictogrammes':
                 label = '';
                 showIcon = true;
                 break;
               case 'non-visibles':
                 label = '';
                 showIcon = false;
                 break;
               default:
                 break;
             }
           }
           
           return displayData.siteWeb ? (
             <div style={{ marginBottom }}>
               <a href={displayData.siteWeb} style={{ ...textStyle, textDecoration: 'none' }}>
                 {label}{showIcon ? icon : ''}{displayData.siteWeb.replace(/^https?:\/\//, '')}
               </a>
             </div>
           ) : null;
         }
       },
       adresse: {
         icon: '📍',
         value: displayData.adresse,
         render: (iconStyle, textStyle) => {
           const marginBottom = style === 'clean' ? '0px' : '0px';
           const fontSize = style === 'clean' ? `${taillePolice - 2}px` : `${taillePolice - 2}px`;
           const icon = style === 'creatif' && emojisActives ? '📍 ' : (iconStyle ? '📍 ' : '');
           
           // Gestion des labels pour le style corporate
           let label = '';
           let showIcon = iconStyle;
           if (style === 'corporate') {
             switch (labelsDisplay) {
               case 'labels':
                 label = 'Adresse: ';
                 showIcon = false;
                 break;
               case 'pictogrammes':
                 label = '';
                 showIcon = true;
                 break;
               case 'non-visibles':
                 label = '';
                 showIcon = false;
                 break;
               default:
                 break;
             }
           }
           
           return displayData.adresse ? (
             <div style={{ color: '#666666', fontSize, marginBottom }}>
               {label}{showIcon ? icon : ''}{displayData.adresse}
             </div>
           ) : null;
         }
       },
       agenda: {
         icon: style === 'creatif' && emojisActives ? '📅' : '📅',
         value: formData.agenda_text,
         render: (iconStyle, textStyle) => {
           const marginBottom = style === 'clean' ? '2px' : '3px';
           const icon = style === 'creatif' && emojisActives ? '📅 ' : (iconStyle ? '📅 ' : '');
           
           // Gestion des labels pour le style corporate
           let label = '';
           let showIcon = iconStyle;
           if (style === 'corporate') {
             switch (labelsDisplay) {
               case 'labels':
                 label = 'Agenda: ';
                 showIcon = false;
                 break;
               case 'pictogrammes':
                 label = '';
                 showIcon = true;
                 break;
               case 'non-visibles':
                 label = '';
                 showIcon = false;
                 break;
               default:
                 break;
             }
           }
           
           const agendaText = formData.agenda_text;
           const agendaUrl = formData.agenda_url;
           
           return agendaText ? (
             <div style={{ marginBottom }}>
               {agendaUrl ? (
                 <a href={agendaUrl} style={{ ...textStyle, textDecoration: 'none' }}>
                   {label}{showIcon ? icon : ''}{agendaText}
                 </a>
               ) : (
                 <span style={textStyle}>
                   {label}{showIcon ? icon : ''}{agendaText}
                 </span>
               )}
             </div>
           ) : null;
         }
       }
    };

    const getStyleForField = (field) => {
      switch (style) {
        case 'creatif':
          return field === 'email' || field === 'siteWeb' || field === 'agenda'
            ? { color: couleurAccent } 
            : field === 'custom'
            ? { color: '#666666' }
            : { color: '#666666' };
        case 'corporate':
          return field === 'email' || field === 'siteWeb' || field === 'agenda'
            ? { color: couleurAccent } 
            : field === 'custom'
            ? { color: '#666666' }
            : { color: '#666666' };
        case 'modern':
        case 'modern-no-icons':
          return field === 'email' || field === 'siteWeb' || field === 'agenda'
            ? { color: couleurAccent } 
            : { color: '#666666' };
        case 'tech':
          return field === 'email' 
            ? { color: '#00d4aa' }
            : field === 'siteWeb'
            ? { color: '#00d4aa' }
            : field === 'custom'
            ? { color: '#cccccc' }
            : { color: '#ffffff' };
        case 'clean':
          return field === 'email' || field === 'siteWeb' || field === 'agenda'
            ? { color: couleurAccent } 
            : field === 'custom'
            ? { color: '#666666' }
            : { color: '#666666' };
        default:
          return field === 'email' || field === 'siteWeb' || field === 'agenda'
            ? { color: couleurAccent } 
            : field === 'custom'
            ? { color: '#666666' }
            : { color: '#666666' };
      }
    };

    const elements = [];
    (contactOrder || []).forEach(field => {
        // Gérer les champs personnalisés (nouveau format objet)
        if (typeof field === 'object' && field.type === 'custom') {
          const customField = customFields.find(cf => cf.id === field.key);
          const customFieldValue = formData[`customField_${field.key}_value`];
          if (customField && customFieldValue) {
            const fieldStyle = getStyleForField('custom');
            const icon = style === 'creatif' && emojisActives ? '📝 ' : '';
            elements.push(
              <div key={field.id} style={{ marginBottom: '3px' }}>
                <span style={fieldStyle}>
                  {icon}{customFieldValue}
                </span>
              </div>
            );
          }
        } else if (typeof field === 'object' && field.type === 'predefined') {
          // Gérer les champs prédéfinis (nouveau format objet)
          const contactField = contactFields[field.key];
          if (contactField && contactField.value) {
            const fieldStyle = getStyleForField(field.key);
            const showIcon = style !== 'clean' && style !== 'modern-no-icons';
            const renderedField = contactField.render(showIcon, fieldStyle);
            if (renderedField) {
              elements.push(
                <div key={field.id}>
                  {renderedField}
                </div>
              );
            }
          }
        } else if (typeof field === 'string') {
          // Gérer l'ancien format (chaînes de caractères) pour compatibilité
          const contactField = contactFields[field];
          if (contactField && contactField.value) {
            const fieldStyle = getStyleForField(field);
            const showIcon = style !== 'clean' && style !== 'modern-no-icons';
            const renderedField = contactField.render(showIcon, fieldStyle);
            if (renderedField) {
              elements.push(
                <div key={field}>
                  {renderedField}
                </div>
              );
            }
          }
        }
     });
     
     return elements;
    };

    // Fonction spéciale pour le style tech avec labels
    const renderContactFieldsTech = () => {
      const contactFields = {
        telephone: { label: 'phone:', value: displayData.telephone, color: '#cccccc' },
        email: { label: 'email:', value: displayData.email, color: couleurAccent, isLink: true, href: `mailto:${displayData.email}` },
        siteWeb: { label: 'web:', value: displayData.siteWeb.replace(/^https?:\/\//, ''), color: couleurAccent, isLink: true, href: displayData.siteWeb },
        adresse: { label: 'address:', value: displayData.adresse, color: '#cccccc' },
        agenda: { 
          label: 'agenda:', 
          value: formData.agenda_text, 
          color: couleurAccent, 
          isLink: !!formData.agenda_url, 
          href: formData.agenda_url 
        }
      };

      const elements = [];
      
      (contactOrder || []).forEach(field => {
        // Gérer les champs personnalisés (nouveau format objet)
        if (typeof field === 'object' && field.type === 'custom') {
          const customField = customFields.find(cf => cf.id === field.key);
          const customFieldValue = formData[`customField_${field.key}_value`];
          if (customField && customFieldValue) {
            elements.push(
              <div key={field.id} style={{ marginBottom: '2px' }}>
                <span style={{ color: '#888888' }}>{customField.label.toLowerCase()}:</span>
                <span style={{ color: '#cccccc', marginLeft: '5px' }}>
                  {customFieldValue}
                </span>
              </div>
            );
          }
        }
        // Gérer les champs prédéfinis (nouveau format objet)
        else if (typeof field === 'object' && field.type === 'predefined') {
          const contactField = contactFields[field.key];
          if (contactField && contactField.value) {
            elements.push(
              <div key={field.id} style={{ marginBottom: field.key === 'adresse' ? '0px' : '2px' }}>
                <span style={{ color: '#888888' }}>{contactField.label}</span>
                {contactField.isLink ? (
                  <a href={contactField.href} style={{ color: contactField.color, textDecoration: 'none', marginLeft: '5px' }}>
                    {contactField.value}
                  </a>
                ) : (
                  <span style={{ color: contactField.color, marginLeft: '5px' }}>
                    {contactField.value}
                  </span>
                )}
              </div>
            );
          }
        }
        // Gérer l'ancien format (chaînes de caractères) pour compatibilité
        else if (typeof field === 'string') {
          const contactField = contactFields[field];
          if (contactField && contactField.value) {
            elements.push(
              <div key={field} style={{ marginBottom: field === 'adresse' ? '0px' : '2px' }}>
                <span style={{ color: '#888888' }}>{contactField.label}</span>
                {contactField.isLink ? (
                  <a href={contactField.href} style={{ color: contactField.color, textDecoration: 'none', marginLeft: '5px' }}>
                    {contactField.value}
                  </a>
                ) : (
                  <span style={{ color: contactField.color, marginLeft: '5px' }}>
                    {contactField.value}
                  </span>
                )}
              </div>
            );
          }
        }
      });
      
      return elements;
    };

    // Fonction spéciale pour le style minimal avec séparateurs
    const renderContactFieldsMinimal = () => {
     const contactFields = {
       telephone: { value: displayData.telephone, color: '#666666' },
       email: { value: displayData.email, color: couleurAccent, isLink: true, href: `mailto:${displayData.email}` },
       siteWeb: { value: displayData.siteWeb.replace(/^https?:\/\//, ''), color: couleurAccent, isLink: true, href: displayData.siteWeb },
       adresse: { value: displayData.adresse, color: '#666666' },
       agenda: { 
         value: formData.agenda_text, 
         color: couleurAccent, 
         isLink: !!formData.agenda_url, 
         href: formData.agenda_url 
       }
     };

     const contactElements = [];
     (contactOrder || []).forEach(field => {
       // Gérer les champs personnalisés (nouveau format objet)
       if (typeof field === 'object' && field.type === 'custom') {
          const customField = customFields.find(cf => cf.id === field.key);
          const customFieldValue = formData[`customField_${field.key}_value`];
          if (customField && customFieldValue) {
            contactElements.push({
                key: field.id,
                element: (
                  <span style={{ color: '#666666' }}>
                    {customFieldValue}
                  </span>
                )
             });
           }
         } else if (typeof field === 'object' && field.type === 'predefined') {
         // Gérer les champs prédéfinis (nouveau format objet)
         const contactField = contactFields[field.key];
         if (contactField && contactField.value) {
           contactElements.push({
               key: field.id,
               element: contactField.isLink ? (
                 <a href={contactField.href} style={{ color: contactField.color, textDecoration: 'none' }}>
                   {contactField.value}
                 </a>
               ) : (
                 <span style={{ color: contactField.color }}>
                   {contactField.value}
                 </span>
               )
           });
           }
         } else if (typeof field === 'string') {
         // Gérer l'ancien format (chaînes de caractères) pour compatibilité
         const contactField = contactFields[field];
         if (contactField && contactField.value) {
           contactElements.push({
               key: field,
               element: contactField.isLink ? (
                 <a href={contactField.href} style={{ color: contactField.color, textDecoration: 'none' }}>
                   {contactField.value}
                 </a>
               ) : (
                 <span style={{ color: contactField.color }}>
                   {contactField.value}
                 </span>
               )
             });
         }
       }
     });

     return (
       <div style={{ marginTop: '8px' }}>
         {contactElements.map((el, index) => (
           <React.Fragment key={el.key}>
             {index > 0 && <span style={{ color: '#666666' }}>{' • '}</span>}
             <span>{el.element}</span>
           </React.Fragment>
         ))}
       </div>
     );
   };

  // Template Moderne - Barre verticale colorée + photo ronde
  const renderModerne = () => {
    // Paramètres personnalisables
    const afficherBarre = formData.afficherBarre || 'oui';
    const epaisseurBarre = formData.epaisseurBarre || 4;
    const photoPosition = formData.photoPosition || 'centrer';
    const photoContour = formData.photoContour || false;
    const photoFormat = formData.photoFormat || 'rond';
    const contactIconsEnabled = formData.contactIconsEnabled || false;

    // Styles de la photo selon le format
    const getPhotoStyle = () => {
      const baseStyle = {
        width: `${photoSize}px`,
        height: `${photoSize}px`,
        objectFit: 'cover'
      };

      switch (photoFormat) {
        case 'carre':
          baseStyle.borderRadius = '0';
          break;
        case 'carre-arrondi':
          baseStyle.borderRadius = '8px';
          break;
        case 'rond':
        default:
          baseStyle.borderRadius = '50%';
          break;
      }

      if (photoContour) {
        baseStyle.border = `2px solid ${couleurAccent}`;
      }

      return baseStyle;
    };

    // Position verticale de la photo
    const getPhotoVerticalAlign = () => {
      switch (photoPosition) {
        case 'haut': return 'top';
        case 'bas': return 'bottom';
        case 'centrer':
        default: return 'middle';
      }
    };

    // Structure selon la position de la barre
    const renderBarreGauche = () => (
      <tr>
        <td style={{ width: `${epaisseurBarre}px`, backgroundColor: couleurAccent, verticalAlign: 'top' }}></td>
        <td style={{ padding: '15px', verticalAlign: 'top' }}>
          {renderContent()}
        </td>
      </tr>
    );





    const renderSansBarre = () => (
      <tr>
        <td style={{ padding: '15px', verticalAlign: 'top' }}>
          {renderContent()}
        </td>
      </tr>
    );

    const renderContent = () => (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '100px', verticalAlign: getPhotoVerticalAlign(), paddingRight: '15px' }}>
              <img 
                src={displayData.photoUrl} 
                alt="Profil"
                style={getPhotoStyle()}
              />
            </td>
            <td style={{ verticalAlign: 'top' }}>
              <div style={{ 
                fontSize: `${taillePolice + 2}px`, 
                fontWeight: 'bold', 
                color: couleurNom,
                marginBottom: '5px'
              }}>
                {displayData.nom}
              </div>
              <div style={{ 
                color: couleurAccent, 
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                {displayData.poste}
              </div>
              {displayData.departement && (
                <div style={{ 
                  color: '#888888',
                  fontSize: `${taillePolice - 1}px`,
                  marginBottom: '8px'
                }}>
                  {displayData.departement}
                </div>
              )}
              <div style={{ 
                color: '#666666',
                marginBottom: '10px'
              }}>
                {displayData.entreprise}
              </div>
              <div>
                {contactIconsEnabled ? renderContactFields('modern') : renderContactFields('modern-no-icons')}
                {renderSocialIcons()}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    );

    return (
      <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', ...baseStyles }}>
        <tbody>
          {afficherBarre === 'oui' ? renderBarreGauche() : renderSansBarre()}
        </tbody>
      </table>
    );
  };

  // Template Moderne 3 - Barre verticale à droite + icônes sociales verticales
  const renderModerne3 = () => {
    // Paramètres personnalisables
    const afficherBarre = formData.moderne3?.afficherBarre || 'oui';
    const epaisseurBarre = formData.moderne3?.epaisseurBarre || 4;
    const photoPosition = formData.moderne3?.photoPosition || 'centrer';
    const photoContour = formData.moderne3?.photoContour || false;
    const photoFormat = formData.moderne3?.photoFormat || 'rond';
    const contactIconsEnabled = formData.moderne3?.contactIconsEnabled || false;
    const socialPosition = formData.moderne3?.socialPosition || 'verticale-droite';
    const police = formData.police || 'Inter';

    // Styles de la photo selon le format
    const getPhotoStyle = () => {
      const baseStyle = {
        width: `${photoSize}px`,
        height: `${photoSize}px`,
        objectFit: 'cover'
      };

      switch (photoFormat) {
        case 'carre':
          baseStyle.borderRadius = '0';
          break;
        case 'carre-arrondi':
          baseStyle.borderRadius = '8px';
          break;
        case 'rond':
        default:
          baseStyle.borderRadius = '50%';
          break;
      }

      if (photoContour) {
        baseStyle.border = `2px solid ${couleurAccent}`;
      }

      return baseStyle;
    };

    // Position verticale de la photo
    const getPhotoVerticalAlign = () => {
      switch (photoPosition) {
        case 'haut': return 'top';
        case 'bas': return 'bottom';
        case 'centrer':
        default: return 'middle';
      }
    };

    // Fonction pour rendre les icônes sociales verticalement
    const renderSocialIconsVertical = () => {
      const activeSocials = [];

      // Utiliser l'ordre défini dans socialOrder si disponible
      if (formData.socialOrder && formData.socialOrder.length > 0) {
        formData.socialOrder.forEach(orderItem => {
          if (orderItem.type === 'predefined') {
            const url = formData[`social_${orderItem.key}`];
            if (url) {
              const style = formData[`social_${orderItem.key}_style`] || 'default';
              activeSocials.push({
                key: orderItem.key,
                url: url,
                networkUrl: orderItem.data.url,
                style: style
              });
            }
          } else if (orderItem.type === 'other') {
            const network = formData.otherNetworks?.find(n => n.id === orderItem.key);
            if (network && network.url && network.url.trim()) {
              const style = formData[`social_other_${network.id}_style`] || 'default';
              activeSocials.push({
                key: `other_${network.id}`,
                url: network.url,
                networkUrl: network.networkUrl,
                name: network.name,
                style: style
              });
            }
          }
        });
      } else {
        // Fallback vers l'ancien comportement
        const socialNetworks = [
          { key: 'linkedin', networkUrl: 'https://linkedin.com' },
          { key: 'facebook', networkUrl: 'https://facebook.com' },
          { key: 'twitter', networkUrl: 'https://x.com' },
          { key: 'instagram', networkUrl: 'https://instagram.com' },
          { key: 'youtube', networkUrl: 'https://youtube.com' },
          { key: 'tiktok', networkUrl: 'https://tiktok.com' }
        ];

        socialNetworks.forEach(network => {
          const url = formData[`social_${network.key}`];
          if (url) {
            const style = formData[`social_${network.key}_style`] || 'default';
            activeSocials.push({
              key: network.key,
              url: url,
              networkUrl: network.networkUrl,
              style: style
            });
          }
        });

        if (formData.otherNetworks) {
          formData.otherNetworks.forEach(network => {
            if (network.url && network.url.trim() && network.name) {
              const style = formData[`social_other_${network.id}_style`] || 'default';
              activeSocials.push({
                key: `other_${network.id}`,
                url: network.url,
                networkUrl: network.networkUrl,
                name: network.name,
                style: style
              });
            }
          });
        }
      }

      if (activeSocials.length === 0) return null;

      const isVertical = socialPosition === 'verticale-droite';
      
      if (isVertical) {
        // Utiliser une structure similaire à celle du HTML généré pour la cohérence
        return (
          <div style={{ display: 'table', width: '100%' }}>
            {activeSocials.map((social, index) => {
              const getNetworkName = (key) => {
                if (key.startsWith('other_')) {
                  const network = formData.otherNetworks?.find(n => `other_${n.id}` === key);
                  return network ? network.name.toLowerCase() : key.replace('other_', '');
                }
                return key;
              };

              const networkName = getNetworkName(social.key);
              const iconSize = formData.iconSize || 32;
              const iconShape = formData.iconShape || 'rond';
              const iconColor = formData.iconColor || '#FFFFFF';
              const iconBackgroundColor = formData.iconBackgroundColor || '#0077B5';
              
              const iconUrl = getOnlineIconUrl(networkName, {
              color: iconColor,
              size: iconSize
            });
            
            if (!iconUrl) return null;
            
            const borderRadius = iconShape === 'rond' ? '50%' : iconShape === 'arrondi' ? '20%' : '0%';
            
            return (
              <div key={social.key || index} style={{ display: 'table-row' }}>
                <div style={{ 
                  display: 'table-cell', 
                  paddingBottom: index < activeSocials.length - 1 ? '8px' : '0',
                  textAlign: 'center'
                }}>
                  <a 
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                  >
                    <img 
                      src={iconUrl}
                      alt={networkName}
                      title={networkName}
                      style={{
                        width: `${iconSize}px`,
                        height: `${iconSize}px`,
                        backgroundColor: iconBackgroundColor,
                        borderRadius: borderRadius,
                        padding: '4px',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Pour les autres positions, utiliser la structure flex normale
    const containerStyle = {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '6px'
    };

    return (
      <div style={containerStyle}>
        {activeSocials.map((social, index) => {
          const getNetworkName = (key) => {
            if (key.startsWith('other_')) {
              const network = formData.otherNetworks?.find(n => `other_${n.id}` === key);
              return network ? network.name.toLowerCase() : key.replace('other_', '');
            }
            return key;
          };

          const networkName = getNetworkName(social.key);
          const iconSize = formData.iconSize || 32;
          const iconShape = formData.iconShape || 'rond';
          const iconColor = formData.iconColor || '#FFFFFF';
          const iconBackgroundColor = formData.iconBackgroundColor || '#0077B5';
          
          const iconUrl = getOnlineIconUrl(networkName, {
            color: iconColor,
            size: iconSize
          });
          
          if (!iconUrl) return null;
          
          const borderRadius = iconShape === 'rond' ? '50%' : iconShape === 'arrondi' ? '20%' : '0%';
          
          return (
            <a 
              key={social.key || index}
              href={social.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <img 
                src={iconUrl}
                alt={networkName}
                title={networkName}
                style={{
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  backgroundColor: iconBackgroundColor,
                  borderRadius: borderRadius,
                  padding: '4px',
                  display: 'block'
                }}
              />
            </a>
          );
        })}
      </div>
    );
    };

    // Rendu différent selon la position des icônes sociales
    if (socialPosition === 'horizontale-bas') {
      return (
        <div style={{ fontFamily: `'${police}', Arial, sans-serif`, ...baseStyles }}>
          <table style={{ width: '450px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                {/* Photo profil */}
                <td style={{ width: '100px', verticalAlign: getPhotoVerticalAlign(), paddingRight: '15px' }}>
                  <img 
                    src={displayData.photoUrl} 
                    alt="Profil"
                    style={getPhotoStyle()}
                  />
                </td>
                
                {/* Contenu texte */}
                <td style={{ verticalAlign: 'top', paddingRight: afficherBarre === 'oui' ? '15px' : '0' }}>
                  <div style={{ 
                    fontSize: `${taillePolice + 2}px`, 
                    fontWeight: 'bold', 
                    color: couleurNom,
                    marginBottom: '5px'
                  }}>
                    {displayData.nom}
                  </div>
                  <div style={{ 
                    color: couleurAccent, 
                    fontWeight: '500',
                    marginBottom: '8px'
                  }}>
                    {displayData.poste}
                  </div>
                  {displayData.departement && (
                    <div style={{ 
                      color: '#888888',
                      fontSize: `${taillePolice - 1}px`,
                      marginBottom: '8px'
                    }}>
                      {displayData.departement}
                    </div>
                  )}
                  <div style={{ 
                    color: '#666666',
                    marginBottom: '10px'
                  }}>
                    {displayData.entreprise}
                  </div>
                  <div style={{ fontSize: `${taillePolice - 1}px`, color: '#666666' }}>
                    {contactIconsEnabled ? renderContactFields('modern') : renderContactFields('modern-no-icons')}
                  </div>
                </td>
                
                {/* Barre verticale */}
                {afficherBarre === 'oui' && (
                  <td style={{ width: `${epaisseurBarre}px`, backgroundColor: couleurAccent, verticalAlign: 'top' }}></td>
                )}
              </tr>
            </tbody>
          </table>
          
          {/* Icônes sociales horizontales en bas */}
          <div style={{ marginTop: '15px', textAlign: 'left' }}>
            {renderSocialIconsVertical()}
          </div>
        </div>
      );
    }

    // Position verticale à droite (par défaut)
    return (
      <table style={{ width: '450px', borderCollapse: 'collapse', fontFamily: `'${police}', Arial, sans-serif`, ...baseStyles }}>
        <tbody>
          <tr>
            {/* Photo profil */}
            <td style={{ width: '100px', verticalAlign: getPhotoVerticalAlign(), paddingRight: '15px' }}>
              <img 
                src={displayData.photoUrl} 
                alt="Profil"
                style={getPhotoStyle()}
              />
            </td>
            
            {/* Contenu texte */}
            <td style={{ verticalAlign: 'top', paddingRight: '15px', width: '250px' }}>
              <div style={{ 
                fontSize: `${taillePolice + 2}px`, 
                fontWeight: 'bold', 
                color: couleurNom,
                marginBottom: '5px'
              }}>
                {displayData.nom}
              </div>
              <div style={{ 
                color: couleurAccent, 
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                {displayData.poste}
              </div>
              {displayData.departement && (
                <div style={{ 
                  color: '#888888',
                  fontSize: `${taillePolice - 1}px`,
                  marginBottom: '8px'
                }}>
                  {displayData.departement}
                </div>
              )}
              <div style={{ 
                color: '#666666',
                marginBottom: '10px'
              }}>
                {displayData.entreprise}
              </div>
              <div style={{ fontSize: `${taillePolice - 1}px`, color: '#666666' }}>
                {contactIconsEnabled ? renderContactFields('modern') : renderContactFields('modern-no-icons')}
              </div>
            </td>
            
            {/* Barre verticale */}
            {afficherBarre === 'oui' && (
              <td style={{ width: `${epaisseurBarre}px`, backgroundColor: couleurAccent, verticalAlign: 'top' }}></td>
            )}
            
            {/* Icônes sociales verticales */}
            <td style={{ verticalAlign: 'middle', textAlign: 'center', width: '50px' }}>
              {renderSocialIconsVertical()}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // Template Moderne 2 - Barre verticale entre photo et texte
  const renderModerne2 = () => {
    const afficherBarre = formData.moderne2?.afficherBarre || 'oui';
    const epaisseurBarre = formData.moderne2?.epaisseurBarre || 4;
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const alignementPhoto = formData.moderne2?.photoPosition || 'centrer';
    const photoContour = formData.moderne2?.photoContour || false;
    const photoFormat = formData.moderne2?.photoFormat || 'rond';
    const contactIconsEnabled = formData.moderne2?.contactIconsEnabled || false;
    const police = formData.police || 'Inter';

    const getPhotoVerticalAlign = () => {
       switch (alignementPhoto) {
         case 'haut': return 'top';
         case 'bas': return 'bottom';
         default: return 'middle';
       }
     };

    const getPhotoStyle = () => {
      let borderRadius;
      switch (photoFormat) {
        case 'carre':
          borderRadius = '0';
          break;
        case 'carre-arrondi':
          borderRadius = '8px';
          break;
        case 'rond':
        default:
          borderRadius = '50%';
          break;
      }

      return {
        width: `${photoSize}px`,
        height: `${photoSize}px`,
        borderRadius,
        objectFit: 'cover',
        border: photoContour ? `3px solid ${couleurAccent}` : 'none'
      };
    };

    const renderContentModerne2 = () => (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: `'${police}', Arial, sans-serif` }}>
        <tbody>
          <tr>
            <td style={{ width: '100px', verticalAlign: getPhotoVerticalAlign(), paddingRight: '15px' }}>
              <img 
                src={displayData.photoUrl} 
                alt="Profil"
                style={getPhotoStyle()}
              />
            </td>
            {afficherBarre === 'oui' && (
              <td style={{ width: `${epaisseurBarre}px`, backgroundColor: couleurAccent, verticalAlign: 'top' }}></td>
            )}
            <td style={{ verticalAlign: 'top', paddingLeft: afficherBarre === 'oui' ? '15px' : '0' }}>
              <div style={{ 
                fontSize: `${taillePolice + 2}px`, 
                fontWeight: 'bold', 
                color: couleurNom,
                marginBottom: '5px'
              }}>
                {displayData.nom}
              </div>
              <div style={{ 
                color: couleurAccent, 
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                {displayData.poste}
              </div>
              {displayData.departement && (
                <div style={{ 
                  color: '#888888',
                  fontSize: `${taillePolice - 1}px`,
                  marginBottom: '8px'
                }}>
                  {displayData.departement}
                </div>
              )}
              <div style={{ 
                color: '#666666',
                marginBottom: '10px'
              }}>
                {displayData.entreprise}
              </div>
              <div style={{ fontSize: `${taillePolice - 1}px`, color: '#666666' }}>
                {renderContactFields(contactIconsEnabled ? 'default' : 'clean')}
              </div>
              {renderSocialIcons()}
            </td>
          </tr>
        </tbody>
      </table>
    );

    return (
      <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', ...baseStyles }}>
        <tbody>
          <tr>
            <td style={{ padding: '15px', verticalAlign: 'top' }}>
              {renderContentModerne2()}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // Template Carte - Fond gris, style carte de visite
  const renderCarte = () => {
    // Options de personnalisation du modèle Carte
    const carteOptions = formData.carte || {};
    const photoFormat = carteOptions.photoFormat || 'carre-arrondi';
    const photoPosition = carteOptions.photoPosition || 'droite';
    const photoContour = carteOptions.photoContour || false;
    const fondCouleur = carteOptions.fondCouleur || '#f8f9fa';
    const borderRadius = carteOptions.borderRadius || 0;

    // Styles de la photo selon le format
    const getPhotoBorderRadius = () => {
      switch (photoFormat) {
        case 'rond': return '50%';
        case 'carre-arrondi': return '8px';
        case 'carre': return '0px';
        default: return '8px';
      }
    };

    // Style du contour de la photo
    const photoBorder = photoContour ? `2px solid ${couleurAccent}` : 'none';

    // Contenu de la carte
    const carteContent = (
      <>
        <div style={{ 
          fontSize: `${taillePolice + 3}px`, 
          fontWeight: 'bold', 
          color: couleurNom,
          marginBottom: '5px'
        }}>
          {displayData.nom}
        </div>
        <div style={{ 
          color: couleurAccent, 
          fontWeight: '500',
          marginBottom: '3px'
        }}>
          {displayData.poste}
        </div>
        {displayData.departement && (
          <div style={{ 
            color: '#888888',
            fontSize: `${taillePolice - 1}px`,
            marginBottom: '3px'
          }}>
            {displayData.departement}
          </div>
        )}
        <div style={{ 
          color: '#666666',
          fontSize: `${taillePolice - 1}px`,
          marginBottom: '10px'
        }}>
          {displayData.entreprise}
        </div>
        <div style={{ fontSize: `${taillePolice - 1}px` }}>
          {renderContactFields('clean')}
          {renderSocialIcons()}
        </div>
      </>
    );

    // Photo
    const photoElement = (
      <img 
        src={displayData.photoUrl} 
        alt="Profil"
        style={{ 
          width: `${photoSize}px`, 
          height: `${photoSize}px`, 
          borderRadius: getPhotoBorderRadius(), 
          objectFit: 'cover',
          border: photoBorder
        }} 
      />
    );

    return (
      <table style={{ 
         width: '400px', 
         height: '150px', 
         borderCollapse: 'collapse', 
         backgroundColor: fondCouleur,
         borderRadius: `${borderRadius}px`,
         ...baseStyles 
       }}>
        <tbody>
          <tr>
            <td style={{ padding: '20px', verticalAlign: 'top' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    {photoPosition === 'gauche' ? (
                      <>
                        <td style={{ width: '110px', verticalAlign: 'top', textAlign: 'left', paddingRight: '15px' }}>
                          {photoElement}
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          {carteContent}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ verticalAlign: 'top' }}>
                          {carteContent}
                        </td>
                        <td style={{ width: '110px', verticalAlign: 'top', textAlign: 'right' }}>
                          {photoElement}
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // Template Minimal - Tout en ligne, épuré
  const renderMinimal = () => {
    // Options de personnalisation du modèle Minimal
    const minimalOptions = formData.minimal || {};
    const photoPosition = minimalOptions.photoPosition || 'gauche';
    const photoFormat = minimalOptions.photoFormat || 'rond';
    const photoBorder = minimalOptions.photoBorder || false;
    const socialPosition = minimalOptions.socialPosition || 'horizontale-droite';

    // Définir le style de la photo selon le format
    let photoStyle = {
      width: `${photoSize}px`, 
      height: `${photoSize}px`, 
      objectFit: 'cover'
    };

    switch (photoFormat) {
      case 'carre':
        photoStyle.borderRadius = '0px';
        break;
      case 'carre-arrondi':
        photoStyle.borderRadius = '8px';
        break;
      case 'rond':
      default:
        photoStyle.borderRadius = '50%';
        break;
    }

    // Ajouter la bordure si activée
    if (photoBorder) {
      photoStyle.border = `2px solid ${couleurAccent}`;
    }

    // Fonction pour rendre les icônes sociales selon la position
    const renderSocialIconsMinimal = () => {
      const socialIcons = renderSocialIcons();
      if (!socialIcons) return null;

      const isVertical = socialPosition.includes('verticale');
      const isLeft = socialPosition.includes('gauche');
      const isCentre = socialPosition.includes('centre');

      let alignItems = 'center';
      let justifyContent = 'flex-end';

      if (isCentre) {
        justifyContent = 'center';
      } else if (isLeft) {
        justifyContent = 'flex-start';
      }

      if (isVertical) {
        alignItems = isLeft ? 'flex-start' : 'flex-end';
      }

      return (
        <div style={{
          display: 'flex',
          flexDirection: isVertical ? 'column' : 'row',
          alignItems: alignItems,
          justifyContent: justifyContent,
          gap: isVertical ? '10px' : '8px',
          marginTop: '8px'
        }}>
          {socialIcons}
        </div>
      );
    };

    // Structure selon la position de la photo
    if (photoPosition === 'haut-milieu') {
      return (
        <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', ...baseStyles }}>
          <tbody>
            <tr>
              <td style={{ padding: '20px', verticalAlign: 'middle', textAlign: 'center' }}>
                <div style={{ marginBottom: '15px' }}>
                  <img 
                    src={displayData.photoUrl} 
                    alt="Profil"
                    style={photoStyle}
                  />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ display: 'inline' }}>
                    <span style={{ 
                      fontSize: `${taillePolice + 1}px`, 
                      fontWeight: 'bold', 
                      color: couleurNom
                    }}>
                      {displayData.nom}
                    </span>
                    <span style={{ color: '#666666' }}>
                      {' • '}{displayData.poste}
                    </span>
                    {displayData.departement && (
                      <span style={{ color: '#888888' }}>
                        {' • '}{displayData.departement}
                      </span>
                    )}
                    <span style={{ color: '#666666' }}>
                      {' • '}{displayData.entreprise}
                    </span>
                  </div>
                  {renderContactFieldsMinimal()}
                  {renderSocialIconsMinimal()}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      );
    }

    // Structure pour position gauche ou droite avec gestion des positions verticales
    const isVerticalSocial = socialPosition.includes('verticale');
    const isSocialLeft = socialPosition.includes('gauche');

    if (isVerticalSocial) {
      // Pour les positions verticales, créer une structure à 3 colonnes
      const socialElement = (
        <td key="social" style={{ 
          width: '40px', 
          verticalAlign: 'middle',
          paddingLeft: isSocialLeft ? '0' : '15px',
          paddingRight: isSocialLeft ? '15px' : '0'
        }}>
          {renderSocialIconsMinimal()}
        </td>
      );

      const photoElement = (
        <td key="photo" style={{ 
          width: '70px', 
          verticalAlign: 'middle', 
          paddingRight: photoPosition === 'gauche' ? '15px' : '0',
          paddingLeft: photoPosition === 'droite' ? '15px' : '0'
        }}>
          <img 
            src={displayData.photoUrl} 
            alt="Profil"
            style={photoStyle}
          />
        </td>
      );

      const contentElement = (
        <td key="content" style={{ verticalAlign: 'middle' }}>
          <div style={{ display: 'inline' }}>
            <span style={{ 
              fontSize: `${taillePolice + 1}px`, 
              fontWeight: 'bold', 
              color: couleurNom
            }}>
              {displayData.nom}
            </span>
            <span style={{ color: '#666666' }}>
              {' • '}{displayData.poste}
            </span>
            {displayData.departement && (
              <span style={{ color: '#888888' }}>
                {' • '}{displayData.departement}
              </span>
            )}
            <span style={{ color: '#666666' }}>
              {' • '}{displayData.entreprise}
            </span>
          </div>
          {renderContactFieldsMinimal()}
        </td>
      );

      // Organiser les éléments selon les positions
      let elements = [];
      if (isSocialLeft && photoPosition === 'gauche') {
        elements = [socialElement, photoElement, contentElement];
      } else if (isSocialLeft && photoPosition === 'droite') {
        elements = [socialElement, contentElement, photoElement];
      } else if (!isSocialLeft && photoPosition === 'gauche') {
        elements = [photoElement, contentElement, socialElement];
      } else {
        elements = [contentElement, photoElement, socialElement];
      }

      return (
        <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', ...baseStyles }}>
          <tbody>
            <tr>
              <td style={{ padding: '20px', verticalAlign: 'middle' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      {elements}
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      );
    }

    // Structure normale pour positions horizontales
    const photoElement = (
      <td style={{ 
        width: '70px', 
        verticalAlign: 'middle', 
        paddingRight: photoPosition === 'gauche' ? '15px' : '0',
        paddingLeft: photoPosition === 'droite' ? '15px' : '0'
      }}>
        <img 
          src={displayData.photoUrl} 
          alt="Profil"
          style={photoStyle}
        />
      </td>
    );

    const contentElement = (
      <td style={{ verticalAlign: 'middle' }}>
        <div style={{ display: 'inline' }}>
          <span style={{ 
            fontSize: `${taillePolice + 1}px`, 
            fontWeight: 'bold', 
            color: couleurNom
          }}>
            {displayData.nom}
          </span>
          <span style={{ color: '#666666' }}>
            {' • '}{displayData.poste}
          </span>
          {displayData.departement && (
            <span style={{ color: '#888888' }}>
              {' • '}{displayData.departement}
            </span>
          )}
          <span style={{ color: '#666666' }}>
            {' • '}{displayData.entreprise}
          </span>
        </div>
        {renderContactFieldsMinimal()}
        {renderSocialIconsMinimal()}
      </td>
    );

    return (
      <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', ...baseStyles }}>
        <tbody>
          <tr>
            <td style={{ padding: '20px', verticalAlign: 'middle' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    {photoPosition === 'gauche' ? (
                      <>
                        {photoElement}
                        {contentElement}
                      </>
                    ) : (
                      <>
                        {contentElement}
                        {photoElement}
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // Template Corporate - Bordures, tableau structuré
  const renderCorporate = () => {
    // Options de personnalisation du modèle Corporate
    const corporateOptions = formData.corporate || {};
    const borderStyle = corporateOptions.borderStyle || 'normal';
    const photoPosition = corporateOptions.photoPosition || 'droite';
    const photoContour = corporateOptions.photoContour || false;

    // Définir les styles selon le type de bordure
    let tableBorder = '';
    let borderRadius = 0;
    let cellTopRadius = 0;
    let cellBottomRadius = 0;

    switch (borderStyle) {
      case 'normal':
        tableBorder = `2px solid ${couleurAccent}`;
        borderRadius = 0;
        cellTopRadius = 0;
        cellBottomRadius = 0;
        break;
      case 'sans-bordure':
        tableBorder = 'none';
        borderRadius = 0;
        cellTopRadius = 0;
        cellBottomRadius = 0;
        break;
      case 'arrondi':
        tableBorder = 'none';
        borderRadius = 12;
        cellTopRadius = 12;
        cellBottomRadius = 12;
        break;
      default:
        tableBorder = `2px solid ${couleurAccent}`;
        borderRadius = 0;
        cellTopRadius = 0;
        cellBottomRadius = 0;
    }

    // Style du contour de la photo
    const photoBorder = photoContour ? `2px solid ${couleurAccent}` : '1px solid #e9ecef';

    // Contenu principal (entreprise et contacts)
    const mainContent = (
      <td style={{ verticalAlign: 'top' }}>
        <div style={{ 
          fontWeight: 'bold',
          color: couleurAccent,
          marginBottom: '8px'
        }}>
          {displayData.entreprise}
        </div>
        <div style={{ fontSize: `${taillePolice - 1}px` }}>
          {renderContactFields('corporate')}
          {renderSocialIcons()}
        </div>
      </td>
    );

    // Photo
    const photoElement = (
      <td style={{ 
        width: '90px', 
        verticalAlign: 'top', 
        textAlign: photoPosition === 'gauche' ? 'left' : 'right',
        paddingRight: photoPosition === 'gauche' ? '15px' : '0',
        paddingLeft: photoPosition === 'droite' ? '15px' : '0'
      }}>
        <img 
          src={displayData.photoUrl} 
          alt="Profil"
          style={{ 
            width: `${photoSize}px`, 
            height: `${photoSize}px`, 
            borderRadius: '4px', 
            objectFit: 'cover',
            border: photoBorder
          }} 
        />
      </td>
    );

    return (
      <table style={{ 
        width: '400px', 
        height: '150px', 
        borderCollapse: 'collapse', 
        border: tableBorder,
        borderRadius: `${borderRadius}px`,
        overflow: 'hidden',
        ...baseStyles 
      }}>
        <tbody>
          <tr>
            <td style={{ 
              backgroundColor: couleurAccent, 
              color: '#ffffff', 
              padding: '10px 20px',
              textAlign: 'center',
              borderTopLeftRadius: `${cellTopRadius}px`,
              borderTopRightRadius: `${cellTopRadius}px`
            }}>
                <div style={{ 
                  fontSize: `${taillePolice + 2}px`, 
                  fontWeight: 'bold'
                }}>
                  {displayData.nom}
                </div>
                <div style={{ fontSize: `${taillePolice - 1}px` }}>
                  {displayData.poste}
                </div>
                {displayData.departement && (
                  <div style={{ 
                    fontSize: `${taillePolice - 2}px`,
                    opacity: '0.9'
                  }}>
                    {displayData.departement}
                  </div>
                )}
            </td>
          </tr>
          <tr>
            <td style={{ 
              padding: '15px 20px',
              borderBottomLeftRadius: `${cellBottomRadius}px`,
              borderBottomRightRadius: `${cellBottomRadius}px`
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    {photoPosition === 'gauche' ? (
                      <>
                        {photoElement}
                        {mainContent}
                      </>
                    ) : (
                      <>
                        {mainContent}
                        {photoElement}
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // Template Créatif - Dégradés, emojis, badges colorés
  const renderCreatif = () => {
    // Options de personnalisation avec valeurs par défaut
    const creatifOptions = formData.creatif || {};
    const formePhoto = creatifOptions.photoFormat || 'rond';
    const positionPhoto = creatifOptions.photoPosition || 'droite';
    const contourPhoto = creatifOptions.photoContour !== false;
    const emojisActives = creatifOptions.emojisEnabled !== false;
    const emplacementIcones = creatifOptions.socialPosition || 'horizontal';
    const couleurFond = creatifOptions.backgroundColor || couleurAccent;

    // Styles de la photo selon les options
    const getPhotoStyles = () => {
      let borderRadius = '50%'; // rond par défaut
      if (formePhoto === 'carre') borderRadius = '0';
      if (formePhoto === 'carre-arrondi') borderRadius = '12px';

      return {
        width: '100px',
        height: '100px',
        borderRadius,
        objectFit: 'cover',
        border: contourPhoto ? `2px solid ${couleurAccent}` : 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      };
    };

    // Rendu des icônes sociales selon l'emplacement
    const renderSocialIconsCreatif = () => {
      if (emplacementIcones === 'vertical') {
        return (
          <div style={{ 
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            alignItems: positionPhoto === 'droite' ? 'flex-start' : 'flex-end'
          }}>
            {renderSocialIcons()}
          </div>
        );
      }
      return renderSocialIcons();
    };

    // Structure selon la position de la photo
    const photoElement = (
      <td style={{ width: '100px', verticalAlign: 'top', textAlign: positionPhoto === 'droite' ? 'right' : 'left' }}>
        <img 
          src={displayData.photoUrl} 
          alt="Profil"
          style={getPhotoStyles()}
        />
        {emplacementIcones === 'vertical' && renderSocialIconsCreatif()}
      </td>
    );

    const textElement = (
      <td style={{ 
        verticalAlign: 'top',
        paddingLeft: positionPhoto === 'gauche' ? '15px' : '0'
      }}>
        <div style={{ 
          fontSize: `${taillePolice + 3}px`, 
          fontWeight: 'bold', 
          color: couleurNom,
          marginBottom: '5px'
        }}>
          <span style={{ verticalAlign: 'baseline' }}>{emojisActives ? '✨ ' : ''}</span>{displayData.nom}
        </div>
        <div style={{ 
          backgroundColor: couleurAccent,
          color: '#ffffff',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: `${taillePolice - 2}px`,
          fontWeight: '500',
          display: 'inline-block',
          marginBottom: '8px'
        }}>
          {displayData.poste}
        </div>
        {displayData.departement && (
          <div style={{ 
            color: '#888888',
            fontSize: `${taillePolice - 1}px`,
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            <span style={{ verticalAlign: 'baseline' }}>{emojisActives ? '🏛️ ' : ''}</span>{displayData.departement}
          </div>
        )}
        <div style={{ 
          color: '#666666',
          marginBottom: '10px',
          fontWeight: '500'
        }}>
          <span style={{ verticalAlign: 'baseline' }}>{emojisActives ? '🏢 ' : ''}</span>{displayData.entreprise}
        </div>
        <div style={{ fontSize: `${taillePolice - 1}px` }}>
          {renderContactFields('creatif')}
          {emplacementIcones === 'horizontal' && renderSocialIcons()}
        </div>
      </td>
    );

    return (
      <table style={{ 
        width: '400px', 
        height: '150px', 
        borderCollapse: 'collapse',
        background: `linear-gradient(135deg, ${couleurFond}22 0%, ${couleurFond}11 100%)`,
        borderRadius: '12px',
        ...baseStyles 
      }}>
        <tbody>
          <tr>
            <td style={{ padding: '20px', verticalAlign: 'top' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    {positionPhoto === 'gauche' ? (
                      <>
                        {React.cloneElement(photoElement, { key: 'photo' })}
                        {React.cloneElement(textElement, { key: 'text' })}
                      </>
                    ) : (
                      <>
                        {React.cloneElement(textElement, { key: 'text' })}
                        {React.cloneElement(photoElement, { key: 'photo' })}
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // Template Tech - Fond noir, style terminal
  const renderTech = () => (
    <table style={{ 
      width: '400px', 
      height: '150px', 
      borderCollapse: 'collapse',
      backgroundColor: '#1a1a1a',
      border: '1px solid #333333',
      borderRadius: '6px',
      color: '#ffffff',
      fontFamily: `'Courier New', monospace`,
      fontSize: `${taillePolice}px`
    }}>
      <tbody>
        <tr>
          <td style={{ padding: '20px', verticalAlign: 'top' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ color: '#00ff00', marginBottom: '8px', fontSize: `${taillePolice - 2}px` }}>
                      $ {formData.tech?.whoami || 'whoami'}
                    </div>
                      <div style={{ 
                        fontSize: `${taillePolice + 1}px`, 
                        fontWeight: 'bold', 
                        color: couleurAccent,
                        marginBottom: '5px'
                      }}>
                         {displayData.nom}
                      </div>
                      <div style={{ 
                        color: '#cccccc',
                        marginBottom: '3px'
                      }}>
                        role: {displayData.poste}
                      </div>
                      {displayData.departement && (
                        <div style={{ 
                          color: '#999999',
                          fontSize: `${taillePolice - 1}px`,
                          marginBottom: '3px'
                        }}>
                          dept: {displayData.departement}
                        </div>
                      )}
                      <div style={{ 
                        color: '#cccccc',
                        marginBottom: '8px'
                      }}>
                        company: {displayData.entreprise}
                      </div>
                    <div style={{ fontSize: `${taillePolice - 1}px` }}>
                        {renderContactFieldsTech()}
                        {renderSocialIcons()}
                    </div>
                  </td>
                    <td style={{ width: '90px', verticalAlign: 'top', textAlign: 'right' }}>
                      <img 
                        src={displayData.photoUrl} 
                        alt="Profil"
                        style={{ 
                          width: `${photoSize}px`, 
                          height: `${photoSize}px`, 
                          borderRadius: formData.tech?.photoFormat === 'rond' ? '50%' : 
                                       formData.tech?.photoFormat === 'carre-arrondi' ? '8px' : '4px',
                          objectFit: 'cover',
                          border: formData.tech?.photoContour !== false ? `2px solid ${couleurAccent}` : 'none',
                          filter: 'grayscale(20%)'
                        }} 
                      />
                    </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // Templates de la bibliothèque - Business
  const renderExecutive = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', border: '2px solid #d4af37', borderRadius: '10px', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ padding: '20px', verticalAlign: 'top', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  {displayData.photoUrl && (
                    <td style={{ width: '100px', verticalAlign: 'top', paddingRight: '15px' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '8px', objectFit: 'cover', border: '3px solid #d4af37' }} />
                    </td>
                  )}
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: `${taillePolice + 3}px`, fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px' }}>{displayData.nom}</div>
                    <div style={{ color: '#d4af37', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', fontSize: `${taillePolice - 1}px` }}>{displayData.poste}</div>
                    <div style={{ color: '#34495e', marginBottom: '10px', fontWeight: '500' }}>{displayData.entreprise}</div>
                    <div>
                      <div style={{ marginBottom: '3px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#d4af37', textDecoration: 'none' }}>✉ {displayData.email}</a></div>
                      <div style={{ marginBottom: '3px', color: '#7f8c8d' }}>☎ {displayData.telephone}</div>
                      <div style={{ marginBottom: '3px' }}><a href={displayData.siteWeb} style={{ color: '#d4af37', textDecoration: 'none' }}>🌐 {displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                      <div style={{ color: '#7f8c8d', fontSize: `${taillePolice - 2}px` }}>📍 {displayData.adresse}</div>
                      {renderSocialIcons()}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  const renderCorporatePlus = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#ffffff', border: '1px solid #e74c3c', borderRadius: '6px', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ width: '6px', backgroundColor: '#e74c3c', verticalAlign: 'top' }}></td>
          <td style={{ padding: '18px', verticalAlign: 'top' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: `${taillePolice + 2}px`, fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px' }}>{displayData.nom}</div>
                    <div style={{ color: '#e74c3c', fontWeight: '500', marginBottom: '8px' }}>{displayData.poste}</div>
                    <div style={{ color: '#7f8c8d', marginBottom: '10px' }}>{displayData.entreprise}</div>
                    <div>
                      <div style={{ marginBottom: '3px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#e74c3c', textDecoration: 'none' }}>{displayData.email}</a></div>
                      <div style={{ marginBottom: '3px', color: '#7f8c8d' }}>{displayData.telephone}</div>
                      <div style={{ marginBottom: '3px' }}><a href={displayData.siteWeb} style={{ color: '#e74c3c', textDecoration: 'none' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                      <div style={{ color: '#7f8c8d', fontSize: `${taillePolice - 2}px` }}>{displayData.adresse}</div>
                      {renderSocialIcons()}
                    </div>
                  </td>
                  {displayData.photoUrl && (
                    <td style={{ width: '90px', verticalAlign: 'top', textAlign: 'right' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e74c3c' }} />
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // Templates Creative
  const renderArtistic = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', background: 'linear-gradient(45deg, #ff6b6b22, #4ecdc422, #45b7d122)', borderRadius: '15px', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ padding: '20px', verticalAlign: 'top' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: `${taillePolice + 3}px`, fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>🎨 {displayData.nom}</div>
                    <div style={{ background: 'linear-gradient(135deg, #ff6b6b, #4ecdc4)', color: '#ffffff', padding: '4px 12px', borderRadius: '20px', fontSize: `${taillePolice - 2}px`, fontWeight: '500', display: 'inline-block', marginBottom: '8px' }}>{displayData.poste}</div>
                    <div style={{ color: '#34495e', marginBottom: '10px', fontWeight: '500' }}>🏢 {displayData.entreprise}</div>
                    <div style={{ fontSize: `${taillePolice - 1}px` }}>
                      <div style={{ marginBottom: '3px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#e74c3c', textDecoration: 'none' }}>📧 {displayData.email}</a></div>
                      <div style={{ marginBottom: '3px', color: '#7f8c8d' }}>📱 {displayData.telephone}</div>
                      <div style={{ marginBottom: '3px' }}><a href={displayData.siteWeb} style={{ color: '#e74c3c', textDecoration: 'none' }}>🌍 {displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                      <div style={{ color: '#7f8c8d', fontSize: `${taillePolice - 2}px` }}>📍 {displayData.adresse}</div>
                      {renderSocialIcons()}
                    </div>
                  </td>
                  {displayData.photoUrl && (
                    <td style={{ width: '100px', verticalAlign: 'top', textAlign: 'right' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '4px solid #ff6b6b', boxShadow: '0 4px 15px rgba(255,107,107,0.3)' }} />
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // Templates Minimal
  const renderClean = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#ffffff', border: '1px solid #ecf0f1', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ padding: '25px', verticalAlign: 'middle' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  {displayData.photoUrl && (
                    <td style={{ width: '80px', verticalAlign: 'middle', paddingRight: '20px' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '1px solid #bdc3c7' }} />
                    </td>
                  )}
                  <td style={{ verticalAlign: 'middle' }}>
                    <div>
                      <span style={{ fontSize: `${taillePolice + 1}px`, fontWeight: '600', color: '#2c3e50' }}>{displayData.nom}</span>
                      <span style={{ color: '#7f8c8d' }}>{displayData.nom ? ' • ' : ''}{displayData.poste}</span>
                      <span style={{ color: '#7f8c8d' }}>{(displayData.nom || displayData.poste) ? ' • ' : ''}{displayData.entreprise}</span>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: `${taillePolice - 1}px` }}>
                      <span><a href={`mailto:${displayData.email}`} style={{ color: '#3498db', textDecoration: 'none' }}>{displayData.email}</a></span>
                      <span style={{ color: '#7f8c8d' }}>{displayData.email ? ' • ' : ''}{displayData.telephone}</span>
                      <span>{(displayData.email || displayData.telephone) ? ' • ' : ''}<a href={displayData.siteWeb} style={{ color: '#3498db', textDecoration: 'none' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></span>
                    </div>
                    <div style={{ marginTop: '5px', color: '#7f8c8d', fontSize: `${taillePolice - 2}px` }}>{displayData.adresse}</div>
                    {renderSocialIcons()}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // Templates Tech
  const renderDigital = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#0f1419', border: '1px solid #00d4aa', borderRadius: '8px', color: '#ffffff', fontFamily: `'Courier New', monospace`, fontSize: `${taillePolice}px` }}>
      <tbody>
        <tr>
          <td style={{ padding: '20px', verticalAlign: 'top' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ color: '#00d4aa', marginBottom: '8px', fontSize: `${taillePolice - 2}px` }}>{'>'} user.profile</div>
                    <div style={{ fontSize: `${taillePolice + 1}px`, fontWeight: 'bold', color: '#00d4aa', marginBottom: '5px' }}>{displayData.nom}</div>
                    <div style={{ color: '#8cc8ff', marginBottom: '3px' }}>{displayData.poste}</div>
                    <div style={{ color: '#ffcc66', marginBottom: '8px' }}>{displayData.entreprise}</div>
                    <div style={{ fontSize: `${taillePolice - 1}px` }}>
                      <div style={{ marginBottom: '2px' }}><span style={{ color: '#ff6b6b' }}>email:</span> <a href={`mailto:${displayData.email}`} style={{ color: '#00d4aa', textDecoration: 'none', marginLeft: '5px' }}>{displayData.email}</a></div>
                      <div style={{ marginBottom: '2px' }}><span style={{ color: '#ff6b6b' }}>phone:</span> <span style={{ color: '#ffffff', marginLeft: '5px' }}>{displayData.telephone}</span></div>
                      <div style={{ marginBottom: '2px' }}><span style={{ color: '#ff6b6b' }}>web:</span> <a href={displayData.siteWeb} style={{ color: '#00d4aa', textDecoration: 'none', marginLeft: '5px' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                      <div><span style={{ color: '#ff6b6b' }}>location:</span> <span style={{ color: '#ffffff', marginLeft: '5px' }}>{displayData.adresse}</span></div>
                      {renderSocialIcons()}
                    </div>
                  </td>
                  {displayData.photoUrl && (
                    <td style={{ width: '90px', verticalAlign: 'top', textAlign: 'right' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '4px', objectFit: 'cover', border: '2px solid #00d4aa', filter: 'contrast(1.1)' }} />
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // Templates Classic
  const renderTraditional = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#fefefe', border: '2px solid #8b4513', borderRadius: '4px', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ padding: '20px', verticalAlign: 'top', background: 'linear-gradient(135deg, #fefefe 0%, #f8f8f8 100%)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top', textAlign: 'center', borderBottom: '1px solid #8b4513', paddingBottom: '10px', marginBottom: '10px' }}>
                    <div style={{ fontSize: `${taillePolice + 2}px`, fontWeight: 'bold', color: '#8b4513', marginBottom: '3px', fontFamily: 'serif' }}>{displayData.nom}</div>
                    <div style={{ color: '#654321', fontStyle: 'italic', marginBottom: '3px' }}>{displayData.poste}</div>
                    <div style={{ color: '#8b4513', fontSize: `${taillePolice - 1}px`, fontWeight: '500' }}>{displayData.entreprise}</div>
                  </td>
                </tr>
                <tr>
                  <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>
                    <div style={{ textAlign: 'center', fontSize: `${taillePolice - 1}px` }}>
                      <div style={{ marginBottom: '2px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#8b4513', textDecoration: 'none' }}>{displayData.email}</a></div>
                      <div style={{ marginBottom: '2px', color: '#654321' }}>{displayData.telephone}</div>
                      <div style={{ marginBottom: '2px' }}><a href={displayData.siteWeb} style={{ color: '#8b4513', textDecoration: 'none' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                      <div style={{ color: '#654321', fontSize: `${taillePolice - 2}px` }}>{displayData.adresse}</div>
                      {renderSocialIcons()}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // Templates Business supplémentaires
  const renderProfessional = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ backgroundColor: '#343a40', padding: '15px 20px', color: '#ffffff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'middle' }}>
                    <div style={{ fontSize: `${taillePolice + 2}px`, fontWeight: 'bold', marginBottom: '3px' }}>{displayData.nom}</div>
                    <div style={{ color: '#adb5bd', fontSize: `${taillePolice - 1}px` }}>{displayData.poste}</div>
                  </td>
                  {displayData.photoUrl && (
                    <td style={{ width: '60px', textAlign: 'right' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '2px solid #ffffff' }} />
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        <tr>
          <td style={{ padding: '15px 20px' }}>
            <div style={{ color: '#495057', fontWeight: '500', marginBottom: '8px' }}>{displayData.entreprise}</div>
            <div style={{ fontSize: `${taillePolice - 1}px` }}>
              <div style={{ marginBottom: '2px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#007bff', textDecoration: 'none' }}>{displayData.email}</a></div>
              <div style={{ marginBottom: '2px', color: '#6c757d' }}>{displayData.telephone}</div>
              <div style={{ marginBottom: '2px' }}><a href={displayData.siteWeb} style={{ color: '#007bff', textDecoration: 'none' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
              <div style={{ color: '#6c757d', fontSize: `${taillePolice - 2}px` }}>{displayData.adresse}</div>
              {renderSocialIcons()}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );

  const renderLuxury = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#1a1a1a', border: '2px solid #ffd700', borderRadius: '12px', color: '#ffffff', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ padding: '20px', verticalAlign: 'top', background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: `${taillePolice + 3}px`, fontWeight: 'bold', color: '#ffd700', marginBottom: '5px', textShadow: '0 0 10px rgba(255,215,0,0.3)' }}>{displayData.nom}</div>
                    <div style={{ color: '#e6e6e6', fontWeight: '300', marginBottom: '8px', letterSpacing: '1px' }}>{displayData.poste}</div>
                    <div style={{ color: '#ffd700', marginBottom: '10px', fontWeight: '500', fontSize: `${taillePolice - 1}px` }}>{displayData.entreprise}</div>
                    <div style={{ fontSize: `${taillePolice - 1}px` }}>
                      <div style={{ marginBottom: '3px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#ffd700', textDecoration: 'none' }}>{displayData.email}</a></div>
                      <div style={{ marginBottom: '3px', color: '#cccccc' }}>{displayData.telephone}</div>
                      <div style={{ marginBottom: '3px' }}><a href={displayData.siteWeb} style={{ color: '#ffd700', textDecoration: 'none' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                      <div style={{ color: '#cccccc', fontSize: `${taillePolice - 2}px` }}>{displayData.adresse}</div>
                      {renderSocialIcons()}
                    </div>
                  </td>
                  {displayData.photoUrl && (
                    <td style={{ width: '90px', verticalAlign: 'top', textAlign: 'right' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '3px solid #ffd700', boxShadow: '0 0 20px rgba(255,215,0,0.3)' }} />
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  // Templates Creative supplémentaires
  const renderColorful = () => (
    <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '15px', color: '#ffffff', ...baseStyles }}>
      <tbody>
        <tr>
          <td style={{ padding: '20px', verticalAlign: 'top' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: `${taillePolice + 3}px`, fontWeight: 'bold', marginBottom: '5px', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>{displayData.nom}</div>
                    <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: `${taillePolice - 2}px`, fontWeight: '500', display: 'inline-block', marginBottom: '8px' }}>{displayData.poste}</div>
                    <div style={{ marginBottom: '10px', fontWeight: '500', opacity: '0.9' }}>{displayData.entreprise}</div>
                    <div style={{ fontSize: `${taillePolice - 1}px` }}>
                      <div style={{ marginBottom: '3px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#ffffff', textDecoration: 'none', opacity: '0.9' }}>{displayData.email}</a></div>
                      <div style={{ marginBottom: '3px', opacity: '0.8' }}>{displayData.telephone}</div>
                      <div style={{ marginBottom: '3px' }}><a href={displayData.siteWeb} style={{ color: '#ffffff', textDecoration: 'none', opacity: '0.9' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                      <div style={{ opacity: '0.8', fontSize: `${taillePolice - 2}px` }}>{displayData.adresse}</div>
                      {renderSocialIcons()}
                    </div>
                  </td>
                  {displayData.photoUrl && (
                    <td style={{ width: '100px', verticalAlign: 'top', textAlign: 'right' }}>
                      <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '4px solid rgba(255,255,255,0.3)', boxShadow: '0 8px 25px rgba(0,0,0,0.2)' }} />
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  const renderPlayful = () => (
     <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#fff5f5', border: '3px dashed #ff6b9d', borderRadius: '20px', ...baseStyles }}>
       <tbody>
         <tr>
           <td style={{ padding: '20px', verticalAlign: 'top' }}>
             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
               <tbody>
                 <tr>
                   <td style={{ verticalAlign: 'top' }}>
                     <div style={{ fontSize: `${taillePolice + 3}px`, fontWeight: 'bold', color: '#ff6b9d', marginBottom: '5px', transform: 'rotate(-1deg)' }}>🌟 {displayData.nom}</div>
                     <div style={{ background: 'linear-gradient(45deg, #ff6b9d, #c44569)', color: '#ffffff', padding: '6px 15px', borderRadius: '25px', fontSize: `${taillePolice - 2}px`, fontWeight: '600', display: 'inline-block', marginBottom: '8px', transform: 'rotate(1deg)' }}>{displayData.poste}</div>
                     <div style={{ color: '#ff6b9d', marginBottom: '10px', fontWeight: '600' }}>🏢 {displayData.entreprise}</div>
                     <div style={{ fontSize: `${taillePolice - 1}px` }}>
                       <div style={{ marginBottom: '3px' }}><a href={`mailto:${displayData.email}`} style={{ color: '#ff6b9d', textDecoration: 'none' }}>💌 {displayData.email}</a></div>
                       <div style={{ marginBottom: '3px', color: '#c44569' }}>📞 {displayData.telephone}</div>
                       <div style={{ marginBottom: '3px' }}><a href={displayData.siteWeb} style={{ color: '#ff6b9d', textDecoration: 'none' }}>🌈 {displayData.siteWeb.replace(/^https?:\/\//, '')}</a></div>
                       <div style={{ color: '#c44569', fontSize: `${taillePolice - 2}px` }}>🗺️ {displayData.adresse}</div>
                       {renderSocialIcons()}
                     </div>
                   </td>
                   {displayData.photoUrl && (
                     <td style={{ width: '100px', verticalAlign: 'top', textAlign: 'right' }}>
                       <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '4px solid #ff6b9d', transform: 'rotate(3deg)', boxShadow: '0 6px 20px rgba(255,107,157,0.3)' }} />
                     </td>
                   )}
                 </tr>
               </tbody>
             </table>
           </td>
         </tr>
       </tbody>
     </table>
   );

   // Templates Minimal supplémentaires
   const renderSimple = () => (
     <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#ffffff', border: 'none', ...baseStyles }}>
       <tbody>
         <tr>
           <td style={{ padding: '30px', verticalAlign: 'middle', textAlign: 'center' }}>
             {displayData.photoUrl && (
               <div style={{ marginBottom: '15px' }}>
                 <img src={displayData.photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover' }} />
               </div>
             )}
             <div style={{ fontSize: `${taillePolice + 2}px`, fontWeight: '300', color: '#2c3e50', marginBottom: '5px' }}>{displayData.nom}</div>
             <div style={{ color: '#7f8c8d', fontSize: `${taillePolice - 1}px`, marginBottom: '3px' }}>{displayData.poste}</div>
             <div style={{ color: '#7f8c8d', fontSize: `${taillePolice - 1}px`, marginBottom: '10px' }}>{displayData.entreprise}</div>
             <div style={{ fontSize: `${taillePolice - 2}px`, color: '#95a5a6' }}>
               <span><a href={`mailto:${displayData.email}`} style={{ color: '#3498db', textDecoration: 'none' }}>{displayData.email}</a></span>
               <span>{displayData.email ? ' | ' : ''}{displayData.telephone}</span>
               <span>{(displayData.email || displayData.telephone) ? ' | ' : ''}<a href={displayData.siteWeb} style={{ color: '#3498db', textDecoration: 'none' }}>{displayData.siteWeb.replace(/^https?:\/\//, '')}</a></span>
             </div>
             <div style={{ color: '#bdc3c7', fontSize: `${taillePolice - 3}px`, marginTop: '5px' }}>{displayData.adresse}</div>
             {renderSocialIcons()}
           </td>
         </tr>
       </tbody>
     </table>
   );

   const renderElegant = () => (
     <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#fafafa', borderLeft: '4px solid #34495e', ...baseStyles }}>
       <tbody>
         <tr>
           <td style={{ padding: '25px', verticalAlign: 'top' }}>
             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
               <tbody>
                 <tr>
                   <td style={{ verticalAlign: 'top', width: '70%' }}>
                     {nom && <div style={{ fontSize: `${taillePolice + 1}px`, fontWeight: '400', color: '#2c3e50', marginBottom: '3px', letterSpacing: '0.5px' }}>{nom}</div>}
                     {poste && <div style={{ color: '#34495e', fontSize: `${taillePolice - 1}px`, marginBottom: '2px', fontStyle: 'italic' }}>{poste}</div>}
                     {entreprise && <div style={{ color: '#7f8c8d', fontSize: `${taillePolice - 1}px`, marginBottom: '12px' }}>{entreprise}</div>}
                     <div style={{ fontSize: `${taillePolice - 2}px`, lineHeight: '1.4' }}>
                       {email && <div style={{ marginBottom: '2px' }}><a href={`mailto:${email}`} style={{ color: '#34495e', textDecoration: 'none' }}>{email}</a></div>}
                       {telephone && <div style={{ marginBottom: '2px', color: '#7f8c8d' }}>{telephone}</div>}
                       {siteWeb && <div style={{ marginBottom: '2px' }}><a href={siteWeb} style={{ color: '#34495e', textDecoration: 'none' }}>{siteWeb.replace(/^https?:\/\//, '')}</a></div>}
                       {adresse && <div style={{ color: '#95a5a6', fontSize: `${taillePolice - 3}px` }}>{adresse}</div>}
                       {renderSocialIcons()}
                     </div>
                   </td>
                   {photoUrl && (
                     <td style={{ width: '30%', verticalAlign: 'top', textAlign: 'right' }}>
                       <img src={photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '4px', objectFit: 'cover', border: '1px solid #bdc3c7' }} />
                     </td>
                   )}
                 </tr>
               </tbody>
             </table>
           </td>
         </tr>
       </tbody>
     </table>
   );

   // Templates Tech supplémentaires
   const renderCyber = () => (
     <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#000000', border: '1px solid #00ff41', borderRadius: '4px', color: '#00ff41', fontFamily: `'Courier New', monospace`, fontSize: `${taillePolice}px` }}>
       <tbody>
         <tr>
           <td style={{ padding: '15px', verticalAlign: 'top' }}>
             <div style={{ color: '#00ff41', marginBottom: '8px', fontSize: `${taillePolice - 3}px` }}>{'$ cat /home/user/profile.txt'}</div>
             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
               <tbody>
                 <tr>
                   <td style={{ verticalAlign: 'top' }}>
                     {nom && <div style={{ fontSize: `${taillePolice + 1}px`, fontWeight: 'bold', marginBottom: '5px' }}>[USER] {nom}</div>}
                     {poste && <div style={{ color: '#ffff00', marginBottom: '3px' }}>[ROLE] {poste}</div>}
                     {entreprise && <div style={{ color: '#ff6600', marginBottom: '8px' }}>[ORG]  {entreprise}</div>}
                     <div style={{ fontSize: `${taillePolice - 1}px` }}>
                       {email && <div style={{ marginBottom: '2px' }}>[MAIL] <a href={`mailto:${email}`} style={{ color: '#00ff41', textDecoration: 'none' }}>{email}</a></div>}
                       {telephone && <div style={{ marginBottom: '2px' }}>[TEL]  {telephone}</div>}
                       {siteWeb && <div style={{ marginBottom: '2px' }}>[WEB]  <a href={siteWeb} style={{ color: '#00ff41', textDecoration: 'none' }}>{siteWeb.replace(/^https?:\/\//, '')}</a></div>}
                       {adresse && <div style={{ fontSize: `${taillePolice - 2}px` }}>[LOC]  {adresse}</div>}
                     </div>
                   </td>
                   {photoUrl && (
                     <td style={{ width: '80px', verticalAlign: 'top', textAlign: 'right' }}>
                       <img src={photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '2px', objectFit: 'cover', border: '1px solid #00ff41', filter: 'contrast(1.2) brightness(0.9)' }} />
                     </td>
                   )}
                 </tr>
               </tbody>
             </table>
           </td>
         </tr>
       </tbody>
     </table>
   );

   const renderNeon = () => (
     <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#1a0033', border: '2px solid #ff00ff', borderRadius: '10px', color: '#ffffff', ...baseStyles }}>
       <tbody>
         <tr>
           <td style={{ padding: '20px', verticalAlign: 'top', background: 'radial-gradient(circle at center, #1a0033 0%, #0d001a 100%)' }}>
             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
               <tbody>
                 <tr>
                   <td style={{ verticalAlign: 'top' }}>
                     {nom && <div style={{ fontSize: `${taillePolice + 2}px`, fontWeight: 'bold', color: '#ff00ff', marginBottom: '5px', textShadow: '0 0 10px #ff00ff' }}>{nom}</div>}
                     {poste && <div style={{ color: '#00ffff', marginBottom: '3px', textShadow: '0 0 5px #00ffff' }}>{poste}</div>}
                     {entreprise && <div style={{ color: '#ffff00', marginBottom: '8px', textShadow: '0 0 5px #ffff00' }}>{entreprise}</div>}
                     <div style={{ fontSize: `${taillePolice - 1}px` }}>
                       {email && <div style={{ marginBottom: '2px' }}><a href={`mailto:${email}`} style={{ color: '#ff00ff', textDecoration: 'none', textShadow: '0 0 3px #ff00ff' }}>{email}</a></div>}
                       {telephone && <div style={{ marginBottom: '2px', color: '#00ffff', textShadow: '0 0 3px #00ffff' }}>{telephone}</div>}
                       {siteWeb && <div style={{ marginBottom: '2px' }}><a href={siteWeb} style={{ color: '#ff00ff', textDecoration: 'none', textShadow: '0 0 3px #ff00ff' }}>{siteWeb.replace(/^https?:\/\//, '')}</a></div>}
                       {adresse && <div style={{ color: '#cccccc', fontSize: `${taillePolice - 2}px` }}>{adresse}</div>}
                     </div>
                   </td>
                   {photoUrl && (
                     <td style={{ width: '90px', verticalAlign: 'top', textAlign: 'right' }}>
                       <img src={photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '50%', objectFit: 'cover', border: '2px solid #ff00ff', boxShadow: '0 0 15px #ff00ff' }} />
                     </td>
                   )}
                 </tr>
               </tbody>
             </table>
           </td>
         </tr>
       </tbody>
     </table>
   );

   // Templates Classic supplémentaires
   const renderVintage = () => (
     <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#f5f5dc', border: '3px double #8b4513', borderRadius: '8px', ...baseStyles }}>
       <tbody>
         <tr>
           <td style={{ padding: '20px', verticalAlign: 'top', background: 'linear-gradient(135deg, #f5f5dc 0%, #f0e68c 100%)' }}>
             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
               <tbody>
                 <tr>
                   <td style={{ verticalAlign: 'top', textAlign: 'center' }}>
                     <div style={{ borderBottom: '2px solid #8b4513', paddingBottom: '8px', marginBottom: '8px' }}>
                       {nom && <div style={{ fontSize: `${taillePolice + 2}px`, fontWeight: 'bold', color: '#8b4513', marginBottom: '3px', fontFamily: 'serif', textShadow: '1px 1px 2px rgba(139,69,19,0.3)' }}>{nom}</div>}
                       {poste && <div style={{ color: '#a0522d', fontStyle: 'italic', fontSize: `${taillePolice - 1}px` }}>{poste}</div>}
                     </div>
                     {entreprise && <div style={{ color: '#8b4513', marginBottom: '8px', fontWeight: '600', fontSize: `${taillePolice - 1}px` }}>{entreprise}</div>}
                     <div style={{ fontSize: `${taillePolice - 2}px`, color: '#654321' }}>
                       {email && <div style={{ marginBottom: '2px' }}><a href={`mailto:${email}`} style={{ color: '#8b4513', textDecoration: 'none' }}>{email}</a></div>}
                       {telephone && <div style={{ marginBottom: '2px' }}>{telephone}</div>}
                       {siteWeb && <div style={{ marginBottom: '2px' }}><a href={siteWeb} style={{ color: '#8b4513', textDecoration: 'none' }}>{siteWeb.replace(/^https?:\/\//, '')}</a></div>}
                       {adresse && <div style={{ fontSize: `${taillePolice - 3}px`, marginTop: '5px' }}>{adresse}</div>}
                     </div>
                   </td>
                 </tr>
               </tbody>
             </table>
           </td>
         </tr>
       </tbody>
     </table>
   );

   const renderFormal = () => (
     <table style={{ width: '400px', height: '150px', borderCollapse: 'collapse', backgroundColor: '#ffffff', border: '1px solid #2c3e50', borderRadius: '0px', ...baseStyles }}>
       <tbody>
         <tr>
           <td style={{ backgroundColor: '#2c3e50', padding: '8px 20px', color: '#ffffff' }}>
             <div style={{ textAlign: 'center', fontSize: `${taillePolice - 2}px`, letterSpacing: '2px', fontWeight: '300' }}>SIGNATURE PROFESSIONNELLE</div>
           </td>
         </tr>
         <tr>
           <td style={{ padding: '20px', verticalAlign: 'top' }}>
             <table style={{ width: '100%', borderCollapse: 'collapse' }}>
               <tbody>
                 <tr>
                   <td style={{ verticalAlign: 'top', width: '70%' }}>
                     {nom && <div style={{ fontSize: `${taillePolice + 1}px`, fontWeight: 'bold', color: '#2c3e50', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '1px' }}>{nom}</div>}
                     {poste && <div style={{ color: '#34495e', marginBottom: '3px', fontSize: `${taillePolice - 1}px` }}>{poste}</div>}
                     {entreprise && <div style={{ color: '#7f8c8d', marginBottom: '10px', fontSize: `${taillePolice - 1}px` }}>{entreprise}</div>}
                     <div style={{ fontSize: `${taillePolice - 2}px`, lineHeight: '1.3' }}>
                       {email && <div style={{ marginBottom: '2px' }}><strong>E:</strong> <a href={`mailto:${email}`} style={{ color: '#2c3e50', textDecoration: 'none' }}>{email}</a></div>}
                       {telephone && <div style={{ marginBottom: '2px', color: '#7f8c8d' }}><strong>T:</strong> {telephone}</div>}
                       {siteWeb && <div style={{ marginBottom: '2px' }}><strong>W:</strong> <a href={siteWeb} style={{ color: '#2c3e50', textDecoration: 'none' }}>{siteWeb.replace(/^https?:\/\//, '')}</a></div>}
                       {adresse && <div style={{ color: '#95a5a6', fontSize: `${taillePolice - 3}px` }}><strong>A:</strong> {adresse}</div>}
                     </div>
                   </td>
                   {photoUrl && (
                     <td style={{ width: '30%', verticalAlign: 'top', textAlign: 'right' }}>
                       <img src={photoUrl} alt="Profil" style={{ width: `${photoSize}px`, height: `${photoSize}px`, borderRadius: '0px', objectFit: 'cover', border: '2px solid #2c3e50' }} />
                     </td>
                   )}
                 </tr>
               </tbody>
             </table>
           </td>
         </tr>
       </tbody>
     </table>
   );

  // Sélection du template
  const renderTemplate = () => {
    switch (template) {
      case 'moderne3':
        return renderModerne3();
      case 'moderne2':
        return renderModerne2();
      case 'carte':
        return renderCarte();
      case 'minimal':
        return renderMinimal();
      case 'corporate':
        return renderCorporate();
      case 'creatif':
        return renderCreatif();
      case 'tech':
        return renderTech();
      // Templates Business
        case 'lib-business-1':
          return renderExecutive();
        case 'lib-business-2':
          return renderCorporatePlus();
        case 'lib-business-3':
          return renderProfessional();
        case 'lib-business-4':
          return renderLuxury();
        case 'lib-business-5':
          return renderModerne(); // Réutilise le template moderne existant
        case 'lib-business-6':
          return renderCarte(); // Réutilise le template carte existant
        case 'lib-business-7':
          return renderCorporate(); // Réutilise le template corporate existant
        // Templates Creative
        case 'lib-creative-1':
          return renderArtistic();
        case 'lib-creative-2':
          return renderColorful();
        case 'lib-creative-3':
          return renderPlayful();
        case 'lib-creative-4':
          return renderCreatif(); // Réutilise le template créatif existant
        case 'lib-creative-5':
          return renderTech(); // Réutilise le template tech existant
       // Templates Minimal
         case 'lib-minimal-1':
           return renderClean();
         case 'lib-minimal-2':
           return renderSimple();
         case 'lib-minimal-3':
           return renderElegant();
         case 'lib-minimal-4':
           return renderMinimal(); // Réutilise le template minimal existant
         case 'lib-minimal-5':
           return renderModerne(); // Variation du template moderne
         // Templates Tech
         case 'lib-tech-1':
           return renderDigital();
         case 'lib-tech-2':
           return renderCyber();
         case 'lib-tech-3':
           return renderNeon();
         case 'lib-tech-4':
           return renderTech(); // Réutilise le template tech existant
         case 'lib-tech-5':
           return renderCreatif(); // Variation créative tech
         // Templates Classic
         case 'lib-classic-1':
           return renderTraditional();
         case 'lib-classic-2':
           return renderVintage();
         case 'lib-classic-3':
           return renderFormal();
         case 'lib-classic-4':
           return renderCorporate(); // Réutilise le template corporate
         case 'lib-classic-5':
           return renderCarte(); // Variation classique du template carte
      default:
        return renderModerne();
    }
  };

  return (
    <div style={{ 
      transform: 'scale(0.8)', 
      transformOrigin: 'center',
      display: 'inline-block'
    }}>
      {renderTemplate()}
      {renderCTAButtons()}
      {banniereUrl && (
        <div style={{ marginTop: '10px', textAlign: 'center' }}>
          {lienBanniere ? (
            <a href={lienBanniere} target="_blank" rel="noopener noreferrer">
              <img 
                src={banniereUrl} 
                alt="Bannière"
                style={{ 
                  maxWidth: `${bannerSize}px`, 
                  height: 'auto',
                  maxHeight: `${Math.round(bannerSize * 0.3)}px`,
                  objectFit: 'contain'
                }} 
              />
            </a>
          ) : (
            <img 
              src={banniereUrl} 
              alt="Bannière"
              style={{ 
                maxWidth: `${bannerSize}px`, 
                height: 'auto',
                maxHeight: `${Math.round(bannerSize * 0.3)}px`,
                objectFit: 'contain'
              }} 
            />
          )}
        </div>
      )}
    </div>
  );
};

// Fonction pour générer le HTML statique pour la copie
SignatureDisplay.generateHTMLForCopy = (formData) => {
  // Valeurs par défaut
  const defaults = {
    nom: 'Jean Dupont',
    poste: 'Directeur Commercial',
    entreprise: 'Mon Entreprise',
    telephone: '06 40 25 23 12',
    email: 'jean.dupont@entreprise.com',
    siteWeb: 'www.entreprise.com',
    adresse: '814 Mission Str 600',
    photoUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
  };

  const displayData = {
    nom: formData.nom || defaults.nom,
    poste: formData.poste || defaults.poste,
    departement: formData.departement || '',
    entreprise: formData.entreprise || defaults.entreprise,
    telephone: formData.telephone || defaults.telephone,
    email: formData.email || defaults.email,
    siteWeb: formData.siteWeb || defaults.siteWeb,
    adresse: formData.adresse || defaults.adresse,
    photoUrl: formData.photoUrl || defaults.photoUrl
  };

  if (formData.template === 'moderne2') {
    const afficherBarre = formData.moderne2?.afficherBarre || 'oui';
    const epaisseurBarre = formData.moderne2?.epaisseurBarre || 4;
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const alignementPhoto = formData.moderne2?.photoPosition || 'centrer';
    const photoContour = formData.moderne2?.photoContour || false;
    const photoFormat = formData.moderne2?.photoFormat || 'rond';
    const contactIconsEnabled = formData.moderne2?.contactIconsEnabled || false;
    const police = formData.police || 'Inter';

    const getPhotoVerticalAlign = () => {
      switch (alignementPhoto) {
        case 'haut': return 'top';
        case 'bas': return 'bottom';
        default: return 'middle';
      }
    };

    const getPhotoStyle = () => {
      let borderRadius = '50%'; // Par défaut rond
      switch (photoFormat) {
        case 'carre':
          borderRadius = '0';
          break;
        case 'carre-arrondi':
          borderRadius = '8px';
          break;
        case 'rond':
        default:
          borderRadius = '50%';
          break;
      }

      return {
        width: `${photoSize}px`,
        height: `${photoSize}px`,
        borderRadius: borderRadius,
        objectFit: 'cover',
        border: photoContour ? `3px solid ${couleurAccent}` : 'none'
      };
    };

    const photoStyle = getPhotoStyle();
    const photoStyleStr = Object.entries(photoStyle)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
      .join('; ');

    const renderContactFields = (style = 'default') => {
      const iconStyle = style === 'clean' ? 'display: none;' : 'display: inline-block; margin-right: 8px; vertical-align: middle;';
      
      let contactHTML = '';
      
      // Gérer les champs selon l'ordre défini
      const contactOrder = formData.contactOrder || ['telephone', 'email', 'siteWeb', 'adresse'];
      const customFields = formData.customFields || [];
      
      contactOrder.forEach(field => {
        // Gérer les champs personnalisés
        if (typeof field === 'object' && field.type === 'custom') {
          const customField = customFields.find(cf => cf.id === field.key);
          const customFieldValue = formData[`customField_${field.key}_value`];
          if (customField && customFieldValue) {
            contactHTML += `<div style="margin-bottom: 3px;"><span style="${iconStyle}">📝</span>${customFieldValue}</div>`;
          }
        }
        // Gérer les champs prédéfinis
        else {
          const fieldKey = typeof field === 'object' ? field.key : field;
          switch (fieldKey) {
            case 'telephone':
              if (displayData.telephone) {
                contactHTML += `<div style="margin-bottom: 3px;"><span style="${iconStyle}">📞</span><a href="tel:${displayData.telephone}" style="color: inherit; text-decoration: none;">${displayData.telephone}</a></div>`;
              }
              break;
            case 'email':
              if (displayData.email) {
                contactHTML += `<div style="margin-bottom: 3px;"><span style="${iconStyle}">✉️</span><a href="mailto:${displayData.email}" style="color: inherit; text-decoration: none;">${displayData.email}</a></div>`;
              }
              break;
            case 'siteWeb':
              if (displayData.siteWeb) {
                contactHTML += `<div style="margin-bottom: 3px;"><span style="${iconStyle}">🌐</span><a href="http://${displayData.siteWeb}" style="color: inherit; text-decoration: none;">${displayData.siteWeb}</a></div>`;
              }
              break;
            case 'adresse':
              if (displayData.adresse) {
                contactHTML += `<div><span style="${iconStyle}">📍</span>${displayData.adresse}</div>`;
              }
              break;
            case 'agenda':
              if (formData.agenda_text) {
                const agendaIcon = `<span style="${iconStyle}">📅</span>`;
                if (formData.agenda_url) {
                  contactHTML += `<div style="margin-bottom: 3px;">${agendaIcon}<a href="${formData.agenda_url}" style="color: inherit; text-decoration: none;">${formData.agenda_text}</a></div>`;
                } else {
                  contactHTML += `<div style="margin-bottom: 3px;">${agendaIcon}${formData.agenda_text}</div>`;
                }
              }
              break;
            default:
              // Champ non reconnu, ne rien faire
              break;
          }
        }
      });
      
      return contactHTML;
    };

    const renderSocialIcons = () => {
      const activeSocials = [];
      
      // Utiliser l'ordre défini dans socialOrder si disponible
      if (formData.socialOrder && formData.socialOrder.length > 0) {
        formData.socialOrder.forEach(orderItem => {
          if (orderItem.type === 'predefined') {
            const url = formData[`social_${orderItem.key}`];
            if (url) {
              activeSocials.push({
                key: orderItem.key,
                url: url,
                networkUrl: orderItem.data.url
              });
            }
          } else if (orderItem.type === 'other') {
            const network = formData.otherNetworks?.find(n => n.id === orderItem.key);
            if (network && network.url && network.url.trim()) {
              activeSocials.push({
                key: `other_${network.id}`,
                url: network.url,
                networkUrl: network.networkUrl,
                name: network.name
              });
            }
          }
        });
      } else {
        // Fallback vers l'ancien comportement
        const socialNetworks = [
          { key: 'linkedin', networkUrl: 'linkedin.com' },
          { key: 'twitter', networkUrl: 'twitter.com' },
          { key: 'facebook', networkUrl: 'facebook.com' },
          { key: 'instagram', networkUrl: 'instagram.com' },
          { key: 'youtube', networkUrl: 'youtube.com' },
          { key: 'tiktok', networkUrl: 'tiktok.com' },
          { key: 'github', networkUrl: 'github.com' },
          { key: 'behance', networkUrl: 'behance.net' },
          { key: 'dribbble', networkUrl: 'dribbble.com' }
        ];
        
        socialNetworks.forEach(network => {
          const url = formData[`social_${network.key}`];
          if (url) {
            activeSocials.push({
              key: network.key,
              url: url,
              networkUrl: network.networkUrl
            });
          }
        });
        
        if (formData.otherNetworks) {
          formData.otherNetworks.forEach(network => {
            if (network.url && network.url.trim() && network.name) {
              activeSocials.push({
                key: `other_${network.id}`,
                url: network.url,
                networkUrl: network.networkUrl,
                name: network.name
              });
            }
          });
        }
      }
      
      if (activeSocials.length === 0) return '';
      
      // Récupérer les paramètres de style des icônes
      const iconSize = formData.iconSize || 32;
      const iconShape = formData.iconShape || 'rond';
      const iconColor = formData.iconColor || '#FFFFFF';
      const iconBackgroundColor = formData.iconBackgroundColor || '#0077B5';
      const borderRadius = iconShape === 'rond' ? '50%' : iconShape === 'arrondi' ? '20%' : '0%';
      
      // Mapping des icônes vers les URLs d'Icons8
      const iconMapping = {
        linkedin: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/linkedin.png`,
        twitter: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/x.png`,
        facebook: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/facebook-new.png`,
        instagram: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/instagram-new.png`,
        youtube: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/youtube-play.png`,
        tiktok: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/tiktok.png`,
        github: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/github.png`,
        behance: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/behance.png`,
        dribbble: `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/dribbble.png`
      };
      
      // Calculer la taille de l'icône (21px par défaut, mais ajustable)
      const displayIconSize = Math.max(16, Math.min(iconSize * 0.65, 32));
      
      let socialHTML = '<div style="margin-top: 8px;">';
      activeSocials.forEach((social, index) => {
        const networkKey = social.key.startsWith('other_') ? social.key.replace('other_', '') : social.key;
        const iconUrl = iconMapping[networkKey] || 'https://img.icons8.com/ios-filled/50/FFFFFF/link.png';
        const networkName = social.name || networkKey;
        
        socialHTML += `
     <a href="${social.url}" target="_blank" style="text-decoration: none;"> 
       <img src="${iconUrl}"  
            alt="${networkName}"  
            title="${networkName}" 
            style="width: ${displayIconSize}px; height: ${displayIconSize}px; background-color: ${iconBackgroundColor}; border-radius: ${borderRadius}; padding: 4px; display: inline-block; margin-right: 8px; vertical-align: middle" /> 
     </a>`;
      });
      socialHTML += '</div>';
      
      return socialHTML;
    };

    return `
      <table style="width: 100%; border-collapse: collapse; font-family: '${police}', Arial, sans-serif;">
        <tbody>
          <tr>
            <td style="width: 100px; vertical-align: ${getPhotoVerticalAlign()}; padding-right: 15px;">
              <img 
                src="${displayData.photoUrl}" 
                alt="Profil"
                style="${photoStyleStr}"
              />
            </td>
            ${afficherBarre === 'oui' ? `<td style="width: ${epaisseurBarre}px; background-color: ${couleurAccent}; vertical-align: top;"></td>` : ''}
            <td style="vertical-align: top; padding-left: ${afficherBarre === 'oui' ? '15px' : '0'};">
              <div style="font-size: ${taillePolice + 2}px; font-weight: bold; color: ${couleurNom}; margin-bottom: 5px;">
                ${displayData.nom}
              </div>
              <div style="color: ${couleurAccent}; font-weight: 500; margin-bottom: 8px;">
                ${displayData.poste}
              </div>
              ${displayData.departement ? `<div style="color: #888888; font-size: ${taillePolice - 1}px; margin-bottom: 8px;">${displayData.departement}</div>` : ''}
              <div style="color: #666666; margin-bottom: 10px;">
                ${displayData.entreprise}
              </div>
              <div style="font-size: ${taillePolice - 1}px; color: #666666;">
                ${renderContactFields(contactIconsEnabled ? 'default' : 'clean')}
              </div>
              ${renderSocialIcons()}
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  if (formData.template === 'minimal') {
    // Options de personnalisation du modèle Minimal
    const minimalOptions = formData.minimal || {};
    const photoPosition = minimalOptions.photoPosition || 'gauche';
    const photoFormat = minimalOptions.photoFormat || 'rond';
    const photoBorder = minimalOptions.photoBorder || false;
    const socialPosition = minimalOptions.socialPosition || 'horizontale-droite';
    
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const police = formData.police || 'Inter';

    // Définir le style de la photo selon le format
    let photoStyleStr = `width: ${photoSize}px; height: ${photoSize}px; object-fit: cover;`;
    switch (photoFormat) {
      case 'carre':
        photoStyleStr += ' border-radius: 0;';
        break;
      case 'carre-arrondi':
        photoStyleStr += ' border-radius: 8px;';
        break;
      case 'rond':
      default:
        photoStyleStr += ' border-radius: 50%;';
        break;
    }

    // Ajouter la bordure si activée
    if (photoBorder) {
      photoStyleStr += ` border: 2px solid ${couleurAccent};`;
    }

    // Fonction pour rendre les champs de contact avec séparateurs
    const renderContactFieldsMinimal = () => {
      const contactOrder = formData.contactOrder || ['telephone', 'email', 'siteWeb', 'adresse'];
      const customFields = formData.customFields || [];
      
      const contactItems = [];
      
      contactOrder.forEach(field => {
        // Gérer les champs personnalisés
        if (typeof field === 'object' && field.type === 'custom') {
          const customField = customFields.find(cf => cf.id === field.key);
          const customFieldValue = formData[`customField_${field.key}_value`];
          if (customField && customFieldValue) {
            contactItems.push(customFieldValue);
          }
        }
        // Gérer les champs prédéfinis
        else {
          const fieldKey = typeof field === 'object' ? field.key : field;
          let value = '';
          let isLink = false;
          let href = '';
          
          switch (fieldKey) {
            case 'telephone':
              if (displayData.telephone) {
                value = displayData.telephone;
                isLink = true;
                href = `tel:${displayData.telephone}`;
              }
              break;
            case 'email':
              if (displayData.email) {
                value = displayData.email;
                isLink = true;
                href = `mailto:${displayData.email}`;
              }
              break;
            case 'siteWeb':
              if (displayData.siteWeb) {
                value = displayData.siteWeb.replace(/^https?:\/\//, '');
                isLink = true;
                href = displayData.siteWeb.startsWith('http') ? displayData.siteWeb : `https://${displayData.siteWeb}`;
              }
              break;
            case 'adresse':
              if (displayData.adresse) {
                value = displayData.adresse;
              }
              break;
            case 'agenda':
              if (formData.agenda_text) {
                value = formData.agenda_text;
                if (formData.agenda_url) {
                  isLink = true;
                  href = formData.agenda_url;
                }
              }
              break;
            default:
              break;
          }
          
          if (value) {
            if (isLink) {
              contactItems.push(`<a href="${href}" style="color: ${fieldKey === 'email' || fieldKey === 'siteWeb' ? couleurAccent : '#666666'}; text-decoration: none;">${value}</a>`);
            } else {
              contactItems.push(value);
            }
          }
        }
      });
      
      return contactItems.join(' • ');
    };

    // Fonction pour rendre les icônes sociales selon la position
    const renderSocialIconsMinimal = () => {
      const socialOrder = formData.socialOrder || [];
      const otherNetworks = formData.otherNetworks || [];
      
      const activeSocials = socialOrder.filter(item => {
        if (item.type === 'predefined') {
          return formData[`social_${item.key}`];
        } else if (item.type === 'custom') {
          const network = otherNetworks.find(n => n.id === item.key);
          return network && formData[`social_other_${item.key}`];
        }
        return false;
      }).map(item => {
        if (item.type === 'predefined') {
          return {
            key: item.key,
            url: formData[`social_${item.key}`],
            name: item.data?.name || item.key
          };
        } else if (item.type === 'custom') {
          const network = otherNetworks.find(n => n.id === item.key);
          return {
            key: `other_${item.key}`,
            url: formData[`social_other_${item.key}`],
            name: network?.name || 'Réseau personnalisé'
          };
        }
        return null;
      });
      
      if (activeSocials.length === 0) return '';
      
      // Mapping des icônes
      const iconMapping = {
        linkedin: 'https://img.icons8.com/ios-filled/50/FFFFFF/linkedin.png',
        twitter: 'https://img.icons8.com/ios-filled/50/FFFFFF/x.png',
        facebook: 'https://img.icons8.com/ios-filled/50/FFFFFF/facebook-new.png',
        instagram: 'https://img.icons8.com/ios-filled/50/FFFFFF/instagram-new.png',
        youtube: 'https://img.icons8.com/ios-filled/50/FFFFFF/youtube-play.png',
        tiktok: 'https://img.icons8.com/ios-filled/50/FFFFFF/tiktok.png',
        github: 'https://img.icons8.com/ios-filled/50/FFFFFF/github.png',
        behance: 'https://img.icons8.com/ios-filled/50/FFFFFF/behance.png',
        dribbble: 'https://img.icons8.com/ios-filled/50/FFFFFF/dribbble.png'
      };
      
      const isVertical = socialPosition.includes('verticale');
      const isHorizontalCenter = socialPosition === 'horizontale-centre';
      const isLeft = socialPosition.includes('gauche');
      
      if (isVertical) {
        let socialHTML = '<table style="border-collapse: collapse; margin-top: 8px;"><tbody>';
        activeSocials.forEach((social, index) => {
          const networkKey = social.key.startsWith('other_') ? social.key.replace('other_', '') : social.key;
          const iconUrl = iconMapping[networkKey] || 'https://img.icons8.com/ios-filled/50/FFFFFF/link.png';
          const networkName = social.name || networkKey;
          
          socialHTML += `
            <tr>
              <td style="padding-bottom: ${index < activeSocials.length - 1 ? '10px' : '0'}; text-align: ${isLeft ? 'left' : 'right'};">
                <a href="${social.url}" target="_blank" style="text-decoration: none;">
                  <img src="${iconUrl}" alt="${networkName}" title="${networkName}" 
                       style="width: 20px; height: 20px; background-color: ${couleurAccent}; border-radius: 50%; padding: 4px;" />
                </a>
              </td>
            </tr>`;
        });
        socialHTML += '</tbody></table>';
        return socialHTML;
      } else {
        const textAlign = isHorizontalCenter ? 'center' : (isLeft ? 'left' : 'right');
        let socialHTML = `<div style="margin-top: 8px; text-align: ${textAlign};">`;
        activeSocials.forEach((social, index) => {
          const networkKey = social.key.startsWith('other_') ? social.key.replace('other_', '') : social.key;
          const iconUrl = iconMapping[networkKey] || 'https://img.icons8.com/ios-filled/50/FFFFFF/link.png';
          const networkName = social.name || networkKey;
          
          socialHTML += `
            <a href="${social.url}" target="_blank" style="text-decoration: none; margin-right: 8px;">
              <img src="${iconUrl}" alt="${networkName}" title="${networkName}" 
                   style="width: 20px; height: 20px; background-color: ${couleurAccent}; border-radius: 50%; padding: 4px; vertical-align: middle;" />
            </a>`;
        });
        socialHTML += '</div>';
        return socialHTML;
      }
    };

    // Structure selon la position de la photo
    if (photoPosition === 'haut-milieu') {
      return `
        <table style="width: 400px; height: 150px; border-collapse: collapse; font-family: '${police}', Arial, sans-serif;">
          <tbody>
            <tr>
              <td style="padding: 20px; vertical-align: middle; text-align: center;">
                <div style="margin-bottom: 15px;">
                  <img src="${displayData.photoUrl}" alt="Profil" style="${photoStyleStr}" />
                </div>
                <div style="text-align: left;">
                  <div style="display: inline;">
                    <span style="font-size: ${taillePolice + 1}px; font-weight: bold; color: ${couleurNom};">
                      ${displayData.nom}
                    </span>
                    <span style="color: #666666;"> • ${displayData.poste}</span>
                    ${displayData.departement ? `<span style="color: #888888;"> • ${displayData.departement}</span>` : ''}
                    <span style="color: #666666;"> • ${displayData.entreprise}</span>
                  </div>
                  <div style="margin-top: 5px; color: #666666; font-size: ${taillePolice - 1}px;">
                    ${renderContactFieldsMinimal()}
                  </div>
                  ${renderSocialIconsMinimal()}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      `;
    }

    // Structure pour position gauche ou droite avec gestion des positions verticales
    const isVerticalSocial = socialPosition.includes('verticale');
    const isSocialLeft = socialPosition.includes('gauche');

    if (isVerticalSocial) {
      // Pour les positions verticales, créer une structure à 3 colonnes
      const socialElement = `
        <td style="width: 40px; vertical-align: middle; padding-left: ${isSocialLeft ? '0' : '15px'}; padding-right: ${isSocialLeft ? '15px' : '0'};">
          ${renderSocialIconsMinimal()}
        </td>
      `;

      const photoElement = `
        <td style="width: 70px; vertical-align: middle; padding-right: ${photoPosition === 'gauche' ? '15px' : '0'}; padding-left: ${photoPosition === 'droite' ? '15px' : '0'};">
          <img src="${displayData.photoUrl}" alt="Profil" style="${photoStyleStr}" />
        </td>
      `;

      const contentElement = `
        <td style="vertical-align: middle;">
          <div style="display: inline;">
            <span style="font-size: ${taillePolice + 1}px; font-weight: bold; color: ${couleurNom};">
              ${displayData.nom}
            </span>
            <span style="color: #666666;"> • ${displayData.poste}</span>
            ${displayData.departement ? `<span style="color: #888888;"> • ${displayData.departement}</span>` : ''}
            <span style="color: #666666;"> • ${displayData.entreprise}</span>
          </div>
          <div style="margin-top: 5px; color: #666666; font-size: ${taillePolice - 1}px;">
            ${renderContactFieldsMinimal()}
          </div>
        </td>
      `;

      // Organiser les éléments selon les positions
      let elements = '';
      if (isSocialLeft && photoPosition === 'gauche') {
        elements = socialElement + photoElement + contentElement;
      } else if (isSocialLeft && photoPosition === 'droite') {
        elements = socialElement + contentElement + photoElement;
      } else if (!isSocialLeft && photoPosition === 'gauche') {
        elements = photoElement + contentElement + socialElement;
      } else {
        elements = contentElement + photoElement + socialElement;
      }

      return `
        <table style="width: 400px; height: 150px; border-collapse: collapse; font-family: '${police}', Arial, sans-serif;">
          <tbody>
            <tr>
              <td style="padding: 20px; vertical-align: middle;">
                <table style="width: 100%; border-collapse: collapse;">
                  <tbody>
                    <tr>
                      ${elements}
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      `;
    }

    // Structure normale pour positions horizontales
    const photoElement = `
      <td style="width: 70px; vertical-align: middle; padding-right: ${photoPosition === 'gauche' ? '15px' : '0'}; padding-left: ${photoPosition === 'droite' ? '15px' : '0'};">
        <img src="${displayData.photoUrl}" alt="Profil" style="${photoStyleStr}" />
      </td>
    `;

    const contentElement = `
      <td style="vertical-align: middle;">
        <div style="display: inline;">
          <span style="font-size: ${taillePolice + 1}px; font-weight: bold; color: ${couleurNom};">
            ${displayData.nom}
          </span>
          <span style="color: #666666;"> • ${displayData.poste}</span>
          ${displayData.departement ? `<span style="color: #888888;"> • ${displayData.departement}</span>` : ''}
          <span style="color: #666666;"> • ${displayData.entreprise}</span>
        </div>
        <div style="margin-top: 5px; color: #666666; font-size: ${taillePolice - 1}px;">
          ${renderContactFieldsMinimal()}
        </div>
        ${renderSocialIconsMinimal()}
      </td>
    `;

    return `
      <table style="width: 400px; height: 150px; border-collapse: collapse; font-family: '${police}', Arial, sans-serif;">
        <tbody>
          <tr>
            <td style="padding: 20px; vertical-align: middle;">
              <table style="width: 100%; border-collapse: collapse;">
                <tbody>
                  <tr>
                    ${photoPosition === 'gauche' ? photoElement + contentElement : contentElement + photoElement}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  if (formData.template === 'moderne') {
    const afficherBarre = formData.afficherBarre || 'oui';
    const epaisseurBarre = formData.epaisseurBarre || 4;
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const photoPosition = formData.photoPosition || 'centrer';
    const photoContour = formData.photoContour || false;
    const photoFormat = formData.photoFormat || 'rond';
    const contactIconsEnabled = formData.contactIconsEnabled || false;
    const police = formData.police || 'Inter';

    const getPhotoVerticalAlign = () => {
      switch (photoPosition) {
        case 'haut': return 'top';
        case 'bas': return 'bottom';
        default: return 'middle';
      }
    };

    // Style de la photo
    let photoStyleStr = `width: ${photoSize}px; height: ${photoSize}px; object-fit: cover;`;
    switch (photoFormat) {
      case 'carre':
        photoStyleStr += ' border-radius: 0;';
        break;
      case 'carre-arrondi':
        photoStyleStr += ' border-radius: 8px;';
        break;
      case 'rond':
      default:
        photoStyleStr += ' border-radius: 50%;';
        break;
    }
    if (photoContour) {
      photoStyleStr += ` border: 2px solid ${couleurAccent};`;
    }

    return `
      <table style="width: 100%; border-collapse: collapse; font-family: '${police}', Arial, sans-serif;">
        <tbody>
          <tr>
            ${afficherBarre === 'oui' ? `<td style="width: ${epaisseurBarre}px; background-color: ${couleurAccent}; vertical-align: top;"></td>` : ''}
            <td style="vertical-align: top; padding: 15px;">
              <table style="width: 100%; border-collapse: collapse;">
                <tbody>
                  <tr>
                    <td style="vertical-align: ${getPhotoVerticalAlign()}; width: ${photoSize + 20}px; padding-right: 15px;">
                      <img 
                        src="${displayData.photoUrl}" 
                        alt="Profil"
                        style="${photoStyleStr}"
                      />
                    </td>
                    <td style="vertical-align: top;">
                      <div style="font-size: ${taillePolice + 2}px; font-weight: bold; color: ${couleurNom}; margin-bottom: 5px;">
                        ${displayData.nom}
                      </div>
                      <div style="color: ${couleurAccent}; font-weight: 500; margin-bottom: 8px;">
                        ${displayData.poste}
                      </div>
                      ${displayData.departement ? `<div style="color: #888888; font-weight: 400; margin-bottom: 8px;">${displayData.departement}</div>` : ''}
                      <div style="color: #666666; margin-bottom: 10px;">
                        ${displayData.entreprise}
                      </div>
                      <div style="font-size: ${taillePolice - 1}px; color: #666666;">
                        ${displayData.telephone && `<div>${contactIconsEnabled ? '📞 ' : ''}${displayData.telephone}</div>`}
                        ${displayData.email && `<div>${contactIconsEnabled ? '✉️ ' : ''}<a href="mailto:${displayData.email}" style="color: inherit; text-decoration: none;">${displayData.email}</a></div>`}
                        ${displayData.siteWeb && `<div>${contactIconsEnabled ? '🌐 ' : ''}<a href="http://${displayData.siteWeb}" style="color: inherit; text-decoration: none;">${displayData.siteWeb}</a></div>`}
                        ${displayData.adresse && `<div>${contactIconsEnabled ? '📍 ' : ''}${displayData.adresse}</div>`}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  // Template Carte
  if (formData.template === 'carte') {
    // Variables communes
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const police = formData.police || 'Inter';

    // Options de personnalisation du modèle Carte
    const carteOptions = formData.carte || {};
    const photoFormat = carteOptions.photoFormat || 'carre-arrondi';
    const photoPosition = carteOptions.photoPosition || 'droite';
    const photoContour = carteOptions.photoContour || false;
    const fondCouleur = carteOptions.fondCouleur || '#f8f9fa';
    const borderRadius = carteOptions.borderRadius || 0;

    // Génération du HTML des champs de contact et icônes sociales
    const contactFieldsHTML = '';
    const socialIconsHTML = '';

    // Styles de la photo selon le format
    const getPhotoBorderRadius = () => {
      switch (photoFormat) {
        case 'rond': return '50%';
        case 'carre-arrondi': return '8px';
        case 'carre': return '0px';
        default: return '8px';
      }
    };

    // Style du contour de la photo
    const photoBorder = photoContour ? `2px solid ${couleurAccent}` : 'none';

    // Contenu de la carte
    const carteContent = `
      <div style="font-size: ${taillePolice + 3}px; font-weight: bold; color: ${couleurNom}; margin-bottom: 5px;">
        ${displayData.nom}
      </div>
      <div style="color: ${couleurAccent}; font-weight: 500; margin-bottom: 3px;">
        ${displayData.poste}
      </div>
      ${displayData.departement ? `<div style="color: #888888; font-weight: 400; margin-bottom: 3px;">${displayData.departement}</div>` : ''}
      <div style="color: #666666; font-size: ${taillePolice - 1}px; margin-bottom: 10px;">
        ${displayData.entreprise}
      </div>
      <div style="font-size: ${taillePolice - 1}px;">
        ${contactFieldsHTML}
        ${socialIconsHTML}
      </div>
    `;

    // Photo
    const photoElement = `
      <img 
        src="${displayData.photoUrl}" 
        alt="Profil"
        style="width: ${photoSize}px; height: ${photoSize}px; border-radius: ${getPhotoBorderRadius()}; object-fit: cover; border: ${photoBorder};" 
      />
    `;

    return `
       <table style="width: 400px; height: 150px; border-collapse: collapse; background-color: ${fondCouleur}; border-radius: ${borderRadius}px; font-family: ${police}, sans-serif;">
        <tbody>
          <tr>
            <td style="padding: 20px; vertical-align: top;">
              <table style="width: 100%; border-collapse: collapse;">
                <tbody>
                  <tr>
                    ${photoPosition === 'gauche' ? `
                      <td style="width: 110px; vertical-align: top; text-align: left; padding-right: 15px;">
                        ${photoElement}
                      </td>
                      <td style="vertical-align: top;">
                        ${carteContent}
                      </td>
                    ` : `
                      <td style="vertical-align: top;">
                        ${carteContent}
                      </td>
                      <td style="width: 110px; vertical-align: top; text-align: right;">
                        ${photoElement}
                      </td>
                    `}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  if (formData.template === 'moderne3') {
    const afficherBarre = formData.moderne3?.afficherBarre || 'oui';
    const epaisseurBarre = formData.moderne3?.epaisseurBarre || 4;
    const photoPosition = formData.moderne3?.photoPosition || 'centrer';
    const photoContour = formData.moderne3?.photoContour || false;
    const photoFormat = formData.moderne3?.photoFormat || 'rond';
    const contactIconsEnabled = formData.moderne3?.contactIconsEnabled || false;
    const socialPosition = formData.moderne3?.socialPosition || 'verticale-droite';
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const police = formData.police || 'Inter';

    // Styles de la photo selon le format
    const getPhotoBorderRadius = () => {
      switch (photoFormat) {
        case 'rond': return '50%';
        case 'carre-arrondi': return '15%';
        case 'carre': return '0%';
        default: return '50%';
      }
    };

    const getPhotoVerticalAlign = () => {
      switch (photoPosition) {
        case 'haut': return 'top';
        case 'bas': return 'bottom';
        case 'centrer': return 'middle';
        default: return 'middle';
      }
    };

    const photoBorder = photoContour ? `3px solid ${couleurAccent}` : 'none';
    const photoStyleStr = `width: ${photoSize}px; height: ${photoSize}px; border-radius: ${getPhotoBorderRadius()}; object-fit: cover; border: ${photoBorder};`;

    // Fonction pour rendre les champs de contact
    const renderContactFields = (style = 'default') => {
      const contactOrder = formData.contactOrder || [];
      const customFields = formData.customFields || [];
      
      if (contactOrder.length === 0) return '';
      
      let contactHTML = '';
      
      contactOrder.forEach((item, index) => {
        let value = '';
        
        if (item.type === 'predefined') {
          value = formData[item.key] || '';
        } else if (item.type === 'custom') {
          const customField = customFields.find(f => f.id === item.key);
          if (customField) {
            value = formData[`customField_${item.key}_value`] || '';
          }
        }
        
        if (value) {
          const isLast = index === contactOrder.length - 1;
          const separator = isLast ? '' : ' | ';
          
          if (item.key === 'email') {
            contactHTML += `<a href="mailto:${value}" style="color: inherit; text-decoration: none;">${value}</a>${separator}`;
          } else if (item.key === 'telephone') {
            contactHTML += `<a href="tel:${value}" style="color: inherit; text-decoration: none;">${value}</a>${separator}`;
          } else if (item.key === 'siteWeb') {
            const url = value.startsWith('http') ? value : `https://${value}`;
            contactHTML += `<a href="${url}" target="_blank" style="color: inherit; text-decoration: none;">${value}</a>${separator}`;
          } else {
            contactHTML += `${value}${separator}`;
          }
        }
      });
      
      return contactHTML;
    };

    // Fonction pour rendre les icônes sociales
    const renderSocialIcons = () => {
      const socialOrder = formData.socialOrder || [];
      const otherNetworks = formData.otherNetworks || [];
      
      const activeSocials = socialOrder.filter(item => {
        if (item.type === 'predefined') {
          return formData[`social_${item.key}`];
        } else if (item.type === 'custom') {
          const network = otherNetworks.find(n => n.id === item.key);
          return network && formData[`social_other_${item.key}`];
        }
        return false;
      }).map(item => {
        if (item.type === 'predefined') {
          return {
            key: item.key,
            url: formData[`social_${item.key}`],
            name: item.data?.name || item.key
          };
        } else if (item.type === 'custom') {
          const network = otherNetworks.find(n => n.id === item.key);
          return {
            key: `other_${item.key}`,
            url: formData[`social_other_${item.key}`],
            name: network?.name || 'Réseau personnalisé'
          };
        }
        return null;
      });
      
      if (activeSocials.length === 0) return '';
      
      // Récupérer les paramètres de style des icônes
      const iconSize = formData.iconSize || 32;
      const iconShape = formData.iconShape || 'rond';
      const iconColor = formData.iconColor || '#FFFFFF';
      const iconBackgroundColor = formData.iconBackgroundColor || '#0077B5';
      const borderRadius = iconShape === 'rond' ? '50%' : iconShape === 'arrondi' ? '20%' : '0%';
      
      // Mapping des icônes vers les URLs d'Icons8
      const iconMapping = {
        linkedin: 'https://img.icons8.com/ios-filled/50/FFFFFF/linkedin.png',
        twitter: 'https://img.icons8.com/ios-filled/50/FFFFFF/x.png',
        facebook: 'https://img.icons8.com/ios-filled/50/FFFFFF/facebook-new.png',
        instagram: 'https://img.icons8.com/ios-filled/50/FFFFFF/instagram-new.png',
        youtube: 'https://img.icons8.com/ios-filled/50/FFFFFF/youtube-play.png',
        tiktok: 'https://img.icons8.com/ios-filled/50/FFFFFF/tiktok.png',
        github: 'https://img.icons8.com/ios-filled/50/FFFFFF/github.png',
        behance: 'https://img.icons8.com/ios-filled/50/FFFFFF/behance.png',
        dribbble: 'https://img.icons8.com/ios-filled/50/FFFFFF/dribbble.png'
      };
      
      // Calculer la taille de l'icône (21px par défaut, mais ajustable)
      const displayIconSize = Math.max(16, Math.min(iconSize * 0.65, 32));
      
      if (socialPosition === 'verticale-droite') {
        let socialHTML = '<table style="border-collapse: collapse; width: 100%;"><tbody>';
        activeSocials.forEach((social, index) => {
          const networkKey = social.key.startsWith('other_') ? social.key.replace('other_', '') : social.key;
          const iconUrl = iconMapping[networkKey] || `https://img.icons8.com/ios-filled/50/${iconColor.replace('#', '')}/link.png`;
          const networkName = social.name || networkKey;
          
          socialHTML += `
       <tr>
         <td style="padding-bottom: ${index < activeSocials.length - 1 ? '8px' : '0'}; text-align: center;">
           <a href="${social.url}" target="_blank" style="text-decoration: none; display: inline-block;"> 
             <img src="${iconUrl}"  
                  alt="${networkName}"  
                  title="${networkName}" 
                  style="width: ${displayIconSize}px; height: ${displayIconSize}px; background-color: ${iconBackgroundColor}; border-radius: ${borderRadius}; padding: 4px; display: block; margin: 0 auto;" /> 
           </a>
         </td>
       </tr>`;
        });
        socialHTML += '</tbody></table>';
        return socialHTML;
      } else {
        let socialHTML = '<div style="margin-top: 8px;">';
        activeSocials.forEach((social, index) => {
          const networkKey = social.key.startsWith('other_') ? social.key.replace('other_', '') : social.key;
          const iconUrl = iconMapping[networkKey] || 'https://img.icons8.com/ios-filled/50/FFFFFF/link.png';
          const networkName = social.name || networkKey;
          
          socialHTML += `
       <a href="${social.url}" target="_blank" style="text-decoration: none;"> 
         <img src="${iconUrl}"  
              alt="${networkName}"  
              title="${networkName}" 
              style="width: ${displayIconSize}px; height: ${displayIconSize}px; background-color: ${iconBackgroundColor}; border-radius: ${borderRadius}; padding: 4px; display: inline-block; margin-right: 8px; vertical-align: middle" /> 
       </a>`;
        });
        socialHTML += '</div>';
        return socialHTML;
      }
    };

    if (socialPosition === 'verticale-droite') {
      return `
        <table style="width: 450px; border-collapse: collapse; font-family: '${police}', Arial, sans-serif;">
          <tbody>
            <tr>
              <td style="width: 100px; vertical-align: ${getPhotoVerticalAlign()}; padding-right: 15px;">
                <img 
                  src="${displayData.photoUrl}" 
                  alt="Profil"
                  style="${photoStyleStr}"
                />
              </td>
              <td style="vertical-align: top; padding-right: 15px; width: 250px;">
                <div style="font-size: ${taillePolice + 2}px; font-weight: bold; color: ${couleurNom}; margin-bottom: 5px;">
                  ${displayData.nom}
                </div>
                <div style="color: ${couleurAccent}; font-weight: 500; margin-bottom: 8px;">
                  ${displayData.poste}
                </div>
                ${displayData.departement ? `<div style="color: #888888; font-weight: 400; margin-bottom: 8px;">${displayData.departement}</div>` : ''}
                <div style="color: #666666; margin-bottom: 10px;">
                  ${displayData.entreprise}
                </div>
                <div style="font-size: ${taillePolice - 1}px; color: #666666;">
                  ${renderContactFields(contactIconsEnabled ? 'default' : 'clean')}
                </div>
              </td>
              ${afficherBarre === 'oui' ? `<td style="width: ${epaisseurBarre}px; background-color: ${couleurAccent}; vertical-align: top;"></td>` : ''}
              <td style="vertical-align: middle; text-align: center; width: 50px;">
                ${renderSocialIcons()}
              </td>
            </tr>
          </tbody>
        </table>
      `;
    } else {
      return `
        <table style="width: 100%; border-collapse: collapse; font-family: '${police}', Arial, sans-serif;">
          <tbody>
            <tr>
              <td style="width: 100px; vertical-align: ${getPhotoVerticalAlign()}; padding-right: 15px;">
                <img 
                  src="${displayData.photoUrl}" 
                  alt="Profil"
                  style="${photoStyleStr}"
                />
              </td>
              ${afficherBarre === 'oui' ? `<td style="width: ${epaisseurBarre}px; background-color: ${couleurAccent}; vertical-align: top;"></td>` : ''}
              <td style="vertical-align: top; padding-left: ${afficherBarre === 'oui' ? '15px' : '0'};">
                <div style="font-size: ${taillePolice + 2}px; font-weight: bold; color: ${couleurNom}; margin-bottom: 5px;">
                  ${displayData.nom}
                </div>
                <div style="color: ${couleurAccent}; font-weight: 500; margin-bottom: 8px;">
                  ${displayData.poste}
                </div>
                ${displayData.departement ? `<div style="color: #888888; font-weight: 400; margin-bottom: 8px;">${displayData.departement}</div>` : ''}
                <div style="color: #666666; margin-bottom: 10px;">
                  ${displayData.entreprise}
                </div>
                <div style="font-size: ${taillePolice - 1}px; color: #666666;">
                  ${renderContactFields(contactIconsEnabled ? 'default' : 'clean')}
                </div>
                ${renderSocialIcons()}
              </td>
            </tr>
          </tbody>
        </table>
      `;
    }
  }

  if (formData.template === 'corporate') {
    const borderStyle = formData.corporate?.borderStyle || 'normal';
    const photoPosition = formData.corporate?.photoPosition || 'droite';
    const photoContour = formData.corporate?.photoContour || false;
    const labelsDisplay = formData.corporate?.labelsDisplay || 'labels';
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const police = formData.police || 'Inter';

    // Définir les styles selon l'option sélectionnée
    let tableBorder, borderRadius, cellTopRadius, cellBottomRadius;
    
    switch (borderStyle) {
      case 'sans-bordure':
        tableBorder = 'none';
        borderRadius = 0;
        cellTopRadius = 0;
        cellBottomRadius = 0;
        break;
      case 'arrondi':
        tableBorder = 'none';
        borderRadius = 12;
        cellTopRadius = 12;
        cellBottomRadius = 12;
        break;
      default: // 'normal'
        tableBorder = `2px solid ${couleurAccent}`;
        borderRadius = 0;
        cellTopRadius = 0;
        cellBottomRadius = 0;
        break;
    }

    const photoBorder = photoContour ? `2px solid ${couleurAccent}` : 'none';

    // Fonction pour rendre les champs de contact
    const renderContactFields = () => {
      const contactOrder = formData.contactOrder || [];
      const customFields = formData.customFields || [];
      
      if (contactOrder.length === 0) return '';
      
      let contactHTML = '';
      
      contactOrder.forEach((item, index) => {
        let value = '';
        let label = '';
        
        if (typeof item === 'object' && item.type === 'custom') {
          const customField = customFields.find(cf => cf.id === item.key);
          const customFieldValue = formData[`customField_${item.key}_value`];
          if (customField && customFieldValue) {
            label = customField.label;
            value = customFieldValue;
          }
        } else if (typeof item === 'object' && item.type === 'predefined') {
          // Gérer les champs prédéfinis (nouveau format objet)
          switch (item.key) {
            case 'telephone':
              if (displayData.telephone) {
                label = 'Tél';
                value = displayData.telephone;
              }
              break;
            case 'email':
              if (displayData.email) {
                label = 'Email';
                value = `<a href="mailto:${displayData.email}" style="color: ${couleurAccent}; text-decoration: none;">${displayData.email}</a>`;
              }
              break;
            case 'siteWeb':
              if (displayData.siteWeb) {
                label = 'Web';
                value = `<a href="${displayData.siteWeb}" style="color: ${couleurAccent}; text-decoration: none;">${displayData.siteWeb.replace(/^https?:\/\//, '')}</a>`;
              }
              break;
            case 'adresse':
              if (displayData.adresse) {
                label = 'Adresse';
                value = displayData.adresse;
              }
              break;
            case 'agenda':
              if (formData.agenda_text) {
                label = 'Agenda';
                if (formData.agenda_url) {
                  value = `<a href="${formData.agenda_url}" style="color: ${couleurAccent}; text-decoration: none;">${formData.agenda_text}</a>`;
                } else {
                  value = formData.agenda_text;
                }
              }
              break;
            default:
              break;
          }
        } else {
          // Gérer l'ancien format (chaînes de caractères) pour compatibilité
          switch (item) {
            case 'telephone':
              if (displayData.telephone) {
                label = 'Tél';
                value = displayData.telephone;
              }
              break;
            case 'email':
              if (displayData.email) {
                label = 'Email';
                value = `<a href="mailto:${displayData.email}" style="color: ${couleurAccent}; text-decoration: none;">${displayData.email}</a>`;
              }
              break;
            case 'siteWeb':
              if (displayData.siteWeb) {
                label = 'Web';
                value = `<a href="${displayData.siteWeb}" style="color: ${couleurAccent}; text-decoration: none;">${displayData.siteWeb.replace(/^https?:\/\//, '')}</a>`;
              }
              break;
            case 'adresse':
              if (displayData.adresse) {
                label = 'Adresse';
                value = displayData.adresse;
              }
              break;
            default:
              // Cas par défaut pour les éléments non reconnus
              break;
          }
        }
        
        if (value) {
          let displayContent = '';
          let icon = '';
          
          // Déterminer l'icône selon le type de champ
          const fieldKey = (typeof item === 'object' && item.type === 'predefined') ? item.key : item;
          switch (fieldKey) {
            case 'telephone':
              icon = '📞';
              break;
            case 'email':
              icon = '📧';
              break;
            case 'siteWeb':
              icon = '🌐';
              break;
            case 'adresse':
              icon = '📍';
              break;
            case 'agenda':
              icon = '📅';
              break;
            default:
              icon = '';
              break;
          }
          
          // Construire le contenu selon l'option labelsDisplay
          switch (labelsDisplay) {
            case 'labels':
              displayContent = `<strong>${label}:</strong> ${value}`;
              break;
            case 'pictogrammes':
              displayContent = `${icon} ${value}`;
              break;
            case 'non-visibles':
              displayContent = value;
              break;
            default:
              displayContent = `<strong>${label}:</strong> ${value}`;
              break;
          }
          
          contactHTML += `
            <div style="margin-bottom: 4px; color: #666666;">
              ${displayContent}
            </div>
          `;
        }
      });
      
      return contactHTML;
    };

    // Fonction pour rendre les icônes sociales
    const renderSocialIcons = () => {
      const activeSocials = Object.entries(formData.reseauxSociaux || {})
        .filter(([key, social]) => social.url && social.url.trim() !== '')
        .map(([key, social]) => ({ key, ...social }));

      if (activeSocials.length === 0) return '';

      let socialHTML = '<div style="margin-top: 10px;">';
      activeSocials.forEach((social, index) => {
        const networkKey = social.key.startsWith('other_') ? social.key.replace('other_', '') : social.key;
        const iconUrl = `https://img.icons8.com/ios-filled/50/${couleurAccent.replace('#', '')}/` + 
          (networkKey === 'linkedin' ? 'linkedin.png' : 
           networkKey === 'twitter' ? 'twitter.png' : 
           networkKey === 'facebook' ? 'facebook.png' : 
           networkKey === 'instagram' ? 'instagram.png' : 
           'link.png');
        
        socialHTML += `
          <a href="${social.url}" target="_blank" style="text-decoration: none; margin-right: 8px;">
            <img src="${iconUrl}" alt="${social.name || networkKey}" style="width: 20px; height: 20px; vertical-align: middle;" />
          </a>
        `;
      });
      socialHTML += '</div>';
      return socialHTML;
    };

    const photoElement = `
      <img 
        src="${displayData.photoUrl}" 
        alt="Profil"
        style="width: ${photoSize}px; height: ${photoSize}px; border-radius: 4px; object-fit: cover; border: ${photoBorder};" 
      />
    `;

    const contentElement = `
      <div style="color: ${couleurNom}; font-size: ${taillePolice + 2}px; font-weight: bold; margin-bottom: 5px;">
        ${displayData.nom}
      </div>
      <div style="color: ${couleurAccent}; font-size: ${taillePolice}px; font-weight: 500; margin-bottom: 8px;">
        ${displayData.poste}
      </div>
      ${displayData.departement ? `<div style="color: #888888; font-size: ${taillePolice - 1}px; font-weight: 400; margin-bottom: 8px;">${displayData.departement}</div>` : ''}
      <div style="color: #666666; font-size: ${taillePolice - 1}px; margin-bottom: 10px;">
        ${displayData.entreprise}
      </div>
      <div style="font-size: ${taillePolice - 1}px;">
        ${renderContactFields()}
        ${renderSocialIcons()}
      </div>
    `;

    return `
      <table style="width: 500px; border-collapse: collapse; border: ${tableBorder}; border-radius: ${borderRadius}px; overflow: hidden; font-family: ${police}, sans-serif;">
        <tbody>
          <tr style="background-color: ${couleurAccent};">
            <td style="padding: 15px; color: white; font-size: ${taillePolice + 4}px; font-weight: bold; border-top-left-radius: ${cellTopRadius}px; border-top-right-radius: ${cellTopRadius}px;">
              ${displayData.nom}
            </td>
          </tr>
          <tr>
            <td style="padding: 20px; border-bottom-left-radius: ${cellBottomRadius}px; border-bottom-right-radius: ${cellBottomRadius}px;">
              <table style="width: 100%; border-collapse: collapse;">
                <tbody>
                  <tr>
                    ${photoPosition === 'gauche' ? `
                      <td style="width: 110px; vertical-align: top; padding-right: 20px;">
                        ${photoElement}
                      </td>
                      <td style="vertical-align: top;">
                        ${contentElement}
                      </td>
                    ` : `
                      <td style="vertical-align: top;">
                        ${contentElement}
                      </td>
                      <td style="width: 110px; vertical-align: top; padding-left: 20px; text-align: right;">
                        ${photoElement}
                      </td>
                    `}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  if (formData.template === 'creatif') {
    const couleurAccent = formData.couleurAccent || '#007bff';
    const taillePolice = parseInt(formData.taillePolice) || 12;
    const couleurNom = formData.couleurNom || '#333333';
    const photoSize = parseInt(formData.photoSize) || 80;
    const police = formData.police || 'Inter';
    
    // Options spécifiques au template créatif
    const formePhoto = formData.creatif?.formePhoto || 'rond';
    const positionPhoto = formData.creatif?.positionPhoto || 'gauche';
    const emojis = formData.creatif?.emojis || false;
    const positionIconesSociales = formData.creatif?.positionIconesSociales || 'bas';
    const couleurFond = formData.creatif?.couleurFond || '#ffffff';

    // Fonction pour rendre les champs de contact avec emojis
    const renderContactFields = () => {
      const contactOrder = formData.contactOrder || [];
      const customFields = formData.customFields || [];
      
      if (contactOrder.length === 0) return '';
      
      let contactHTML = '';
      
      contactOrder.forEach((item, index) => {
        let value = '';
        let emoji = '';
        
        if (typeof item === 'object' && item.type === 'custom') {
          const customField = customFields.find(cf => cf.id === item.key);
          const customFieldValue = formData[`customField_${item.key}_value`];
          if (customField && customFieldValue) {
            emoji = emojis ? '📝 ' : '';
            value = customFieldValue;
          }
        } else {
          switch (item) {
            case 'telephone':
              if (displayData.telephone) {
                emoji = emojis ? '📞 ' : '';
                value = displayData.telephone;
              }
              break;
            case 'email':
              if (displayData.email) {
                emoji = emojis ? '✉️ ' : '';
                value = displayData.email;
              }
              break;
            case 'siteWeb':
              if (displayData.siteWeb) {
                emoji = emojis ? '🌐 ' : '';
                value = displayData.siteWeb;
              }
              break;
            case 'adresse':
              if (displayData.adresse) {
                emoji = emojis ? '📍 ' : '';
                value = displayData.adresse;
              }
              break;
            default:
              break;
          }
        }
        
        if (value) {
          contactHTML += `<div style="margin-bottom: 4px; color: #666666; font-size: ${taillePolice - 1}px;">${emoji}${value}</div>`;
        }
      });
      
      return contactHTML;
    };

    // Fonction pour rendre les icônes sociales
    const renderSocialIcons = () => {
      const activeSocials = Object.entries(formData.reseauxSociaux || {})
        .filter(([key, social]) => social.url && social.url.trim() !== '')
        .map(([key, social]) => ({ key, ...social }));

      if (activeSocials.length === 0) return '';

      let socialHTML = '<div style="margin-top: 10px;">';
      activeSocials.forEach((social, index) => {
        const networkKey = social.key.startsWith('other_') ? social.key.replace('other_', '') : social.key;
        const iconUrl = `https://img.icons8.com/ios-filled/50/${couleurAccent.replace('#', '')}/` + 
          (networkKey === 'linkedin' ? 'linkedin.png' : 
           networkKey === 'twitter' ? 'twitter.png' : 
           networkKey === 'facebook' ? 'facebook.png' : 
           networkKey === 'instagram' ? 'instagram.png' : 
           'link.png');
        
        socialHTML += `
          <a href="${social.url}" target="_blank" style="text-decoration: none; margin-right: 8px;">
            <img src="${iconUrl}" alt="${social.name || networkKey}" style="width: 20px; height: 20px; vertical-align: middle;" />
          </a>
        `;
      });
      socialHTML += '</div>';
      return socialHTML;
    };

    // Style de la photo selon la forme
    const getPhotoBorderRadius = () => {
      switch (formePhoto) {
        case 'carre': return '0';
        case 'carre-arrondi': return '8px';
        case 'rond':
        default: return '50%';
      }
    };

    const photoElement = `
      <img 
        src="${displayData.photoUrl}" 
        alt="Profil"
        style="width: ${photoSize}px; height: ${photoSize}px; border-radius: ${getPhotoBorderRadius()}; object-fit: cover; border: 2px solid ${couleurAccent};" 
      />
    `;

    const contentElement = `
      <div style="color: ${couleurNom}; font-size: ${taillePolice + 4}px; font-weight: bold; margin-bottom: 8px;">
        ${displayData.nom}
      </div>
      <div style="color: ${couleurAccent}; font-size: ${taillePolice + 1}px; font-weight: 500; margin-bottom: 5px;">
        ${displayData.poste}
      </div>
      ${displayData.departement ? `<div style="color: #888888; font-size: ${taillePolice}px; font-weight: 400; margin-bottom: 5px;">${displayData.departement}</div>` : ''}
      <div style="color: #666666; font-size: ${taillePolice}px; margin-bottom: 10px;">
        ${displayData.entreprise}
      </div>
      <div>
        ${renderContactFields()}
        ${positionIconesSociales === 'bas' ? renderSocialIcons() : ''}
      </div>
    `;

    return `
      <table style="width: 500px; border-collapse: collapse; background-color: ${couleurFond}; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.1); font-family: ${police}, sans-serif;">
        <tbody>
          <tr>
            <td style="padding: 25px;">
              <table style="width: 100%; border-collapse: collapse;">
                <tbody>
                  <tr>
                    ${positionPhoto === 'gauche' ? `
                      <td style="width: 110px; vertical-align: top; padding-right: 20px;">
                        ${photoElement}
                      </td>
                      <td style="vertical-align: top;">
                        ${contentElement}
                      </td>
                    ` : `
                      <td style="vertical-align: top;">
                        ${contentElement}
                      </td>
                      <td style="width: 110px; vertical-align: top; padding-left: 20px; text-align: right;">
                        ${photoElement}
                      </td>
                    `}
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  // Pour les autres templates, retourner une chaîne vide ou gérer selon les besoins
  return '';
};

export default SignatureDisplay;
